/**
 * Bundle Selector JavaScript
 * Handles product selection, variant management, and cart integration
 * Updated: 2025-01-23 v25 - Multiple accordions with shared collection and title filters
 */

// Prevent duplicate class declaration if script is loaded multiple times
if (typeof window.BundleSelector === 'undefined') {
  window.BundleSelector = class BundleSelector {
    constructor(element, settings) {
      this.element = element;
      // A theme may relocate one of our sections in the DOM (NovaFronten lifts
      // the "Griffe" section into its own step). Runtime lookups must survive
      // that, so fall back to a document-wide search scoped by our block id.
      // All elements belonging to THIS block. After a relocation there can be
      // more than one (the theme re-wraps the moved part with the same id), so
      // collect them all - taking only the first would return the original
      // wrapper, which no longer contains the moved section.
      this.blockScopes = () => {
        const id = this.element.getAttribute && this.element.getAttribute('data-block-id');
        if (!id) return [this.element];
        const esc = window.CSS && CSS.escape ? CSS.escape(id) : id;
        // NF: Teile, die das Theme aus dem Block herausschiebt (Aktionsleiste, nf-front-accordion),
        // tragen data-nf-block-scope – so findet cacheDOM() sie unabhängig von der Ladereihenfolge.
        const list = Array.from(document.querySelectorAll(`[data-block-id="${esc}"], [data-nf-block-scope="${esc}"]`));
        if (!list.includes(this.element)) list.unshift(this.element);
        return list;
      };
      // Deliberately NO unscoped document fallback: with two blocks on a page
      // that would let one instance grab the other's nodes.
      this.scopedQuery = (sel) => {
        for (const scope of this.blockScopes()) {
          const hit = scope.querySelector(sel);
          if (hit) return hit;
        }
        return null;
      };
      this.scopedQueryAll = (sel) => {
        const seen = new Set();
        for (const scope of this.blockScopes()) {
          scope.querySelectorAll(sel).forEach(el => seen.add(el));
        }
        return Array.from(seen);
      };
      this.settings = settings;
    this.selectedProducts = new Map(); // Use Map with variantId as key
    this.availableProducts = [];
    this.allCollectionProducts = []; // Store all products from collection
    this.userModifiedVariants = new Set(); // Track products where user manually changed variants
    this.currentSectionId = null; // Track which section opened the modal
    this.sections = settings.sections || []; // Array of section configurations
    this.isModalFreshOpen = false; // Track if modal just opened (fresh state)
    this.productsLoadedPromise = null; // Track products loading state
    this.selectedColorFilter = null; // Track selected color filter
    this.selectedBackgroundFilter = null; // Track selected background color (Hintergrund Farbe der Griffe)
    this.availableColors = []; // Track available colors from metafields
    this.currentSearchTerm = ''; // Track current search term

    this.init();
  }

  /**
   * Helper to get option name from product.options array
   * Shopify API returns options as objects with {name, position, values}
   */
  getOptionName(option) {
    if (!option) return null;
    // If option is an object with name property, extract it
    if (typeof option === 'object' && option.name) {
      return String(option.name);
    }
    // If option is already a string, use it directly
    return String(option);
  }

  /**
   * NF 2026-10-23: Varianten über 250 ohne Storefront-Token im Seitenquelltext (Felix 23.10.: „yes but in a way not to
   * expose it on the internet“; der alte Token galt seit 22.10. nicht mehr → 43 Produkte waren still auf 250 gekürzt,
   * 60×80 und größer sowie 45×80 fehlten). /products/<handle>.js liefert höchstens 250 Varianten; der Rest kommt
   * seitenweise (250 je Seite, Reihenfolge wie im Shop) aus der Theme-Vorlage templates/product.nf-varianten.liquid:
   *   /products/<handle>?view=nf-varianten&page=N → { items, pages, variants: [{ id, title, option1–3, price,
   *   compare_at_price, available }] } – gleiche Felder wie .js.
   * Ergebnis { variants } (vollständig, geprüft gegen „items“) oder { variants: null, status }.
   */
  async fetchAllVariantsViaTheme(handle, firstVariants) {
    const base = `/products/${encodeURIComponent(handle)}?view=nf-varianten&page=`;
    const ok = (r) => !!(r && r.data && Array.isArray(r.data.variants));
    const r2 = await this.nfThemeJson(base + 2);
    if (!ok(r2)) return { variants: null, status: r2.status };
    const total = Number(r2.data.items) || 0;
    const pages = Math.min(Number(r2.data.pages) || 1, 20);
    const rest = [];
    for (let p = 3; p <= pages; p++) rest.push(this.nfThemeJson(base + p));
    const more = await Promise.all(rest);
    const bad = more.find(r => !ok(r));
    if (bad) return { variants: null, status: bad.status };
    const merge = (lists) => {
      const seen = new Set();
      const out = [];
      lists.forEach(list => list.forEach(v => {
        const id = v && v.id ? String(v.id) : '';
        if (id && !seen.has(id)) { seen.add(id); out.push(v); }
      }));
      return out;
    };
    const tail = [r2.data.variants, ...more.map(r => r.data.variants)];
    let all = merge([firstVariants || [], ...tail]);
    if (all.length !== total) {
      // .js und Vorlage sortieren anders (sollte nicht vorkommen) → Seite 1 auch aus der Vorlage, .js-Objekte behalten
      const r1 = await this.nfThemeJson(base + 1);
      if (!ok(r1)) return { variants: null, status: r1.status };
      const js = new Map((firstVariants || []).map(v => [String(v.id), v]));
      all = merge([r1.data.variants, ...tail]).map(v => js.get(String(v.id)) || v);
    }
    if (!total || all.length !== total) {
      console.warn(`[NF] Varianten unvollständig für ${handle}: ${all.length}/${total}`);
      return { variants: null, status: -2 };
    }
    return { variants: all };
  }

  /**
   * NF 2026-10-23: JSON aus einer Theme-Vorlage (?view=…). Höchstens 8 Abrufe gleichzeitig (nfLimit) – der Shop sperrt
   * eine IP bei vielen Abrufen kurz mit 429 „local_rate_limited“ (Retry-After 60 s); Iconic Matt braucht bis zu 44 Seiten.
   * Ein zweiter Versuch nach 800 ms, nicht nach 429 und nicht, wenn statt JSON eine Seite kommt (Vorlage fehlt →
   * Shopify zeigt die normale Produktseite; dann für den Rest der Seite direkt der Rückfall). → { data } oder { status }
   */
  async nfThemeJson(url) {
    let status = 0;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise(r => setTimeout(r, 800));
      try {
        const res = await this.nfLimit(async () => {
          const r = await fetch(url, { credentials: 'same-origin' });
          return { status: r.status, text: r.ok ? await r.text() : '' };
        });
        status = res.status;
        if (status >= 200 && status < 300) {
          const text = res.text.trim();
          if (text.charAt(0) !== '{') {
            this._nfViewBroken = true;
            console.warn(`[NF] ${url}: keine JSON-Antwort (Theme-Vorlage fehlt?)`);
            return { status: -3 };
          }
          return { data: JSON.parse(text) };
        }
        console.warn(`[NF] ${url}: HTTP ${status}${status === 429 || attempt ? '' : ' – neuer Versuch'}`);
        if (status === 429) break;
      } catch (e) {
        status = -1;
        console.warn(`[NF] ${url}${attempt ? '' : ' – neuer Versuch'}:`, e);
      }
    }
    return { status };
  }

  /** NF 2026-10-23: Warteschlange – höchstens 8 Theme-Abrufe (nfThemeJson) gleichzeitig (Iconic Matt: 44 Seiten ≈ 2 s). */
  nfLimit(task) {
    const q = this._nfQueue || (this._nfQueue = { active: 0, waiting: [] });
    return new Promise((resolve, reject) => {
      const run = () => {
        q.active++;
        Promise.resolve().then(task).then(resolve, reject).then(() => {
          q.active--;
          const next = q.waiting.shift();
          if (next) next();
        });
      };
      if (q.active < 8) run(); else q.waiting.push(run);
    });
  }

  /**
   * NF 2026-10-23: Storefront API OHNE Token (Shopify „tokenless access“: nur öffentliche Produkt-/Sammlungsdaten,
   * Kostenlimit 1000 je Abfrage – Liste ~24, 250 Varianten ~57). Gleiche Domain wie der Shop, kein Token im
   * Seitenquelltext, nichts zu rotieren. Ein zweiter Versuch nach 800 ms, nicht nach 429/4xx. → { data } oder { status }
   */
  async nfStorefront(query, variables) {
    let status = 0;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise(r => setTimeout(r, 800));
      try {
        const res = await fetch('/api/2026-10/graphql.json', {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query, variables })
        });
        status = res.status;
        if (res.ok) {
          const j = await res.json();
          if (j && j.data && !j.errors) return { data: j.data };
          console.warn('[NF] Storefront API (ohne Token):', j && j.errors);
          return { status: -4 };
        }
        console.warn(`[NF] Storefront API (ohne Token): HTTP ${status}${status === 429 || attempt ? '' : ' – neuer Versuch'}`);
        if (status >= 400 && status < 500) break;
      } catch (e) {
        status = -1;
        console.warn(`[NF] Storefront API (ohne Token)${attempt ? '' : ' – neuer Versuch'}:`, e);
      }
    }
    return { status };
  }

  /**
   * NF 2026-10-23: Rückfall, wenn die Theme-Vorlage nf-varianten nicht liefern konnte (z. B. 429): alle Varianten über die
   * Storefront API ohne Token (wie bis 22.10. mit Token), seitenweise je 250, Preise im Markt des Besuchers (@inContext).
   * product = Daten aus /products/<handle>.js (Optionsnamen, erste 250 Varianten). → vollständige Liste oder null.
   */
  async fetchAllVariantsViaStorefront(handle, product) {
    const country = window.Shopify && /^[A-Z]{2}$/.test(String(window.Shopify.country || '')) ? window.Shopify.country : '';
    const query = `query($h:String!,$c:String)${country ? ` @inContext(country: ${country})` : ''}{product(handle:$h){` +
      'variantsCount{count} variants(first:250,after:$c){pageInfo{hasNextPage endCursor} ' +
      'nodes{id title availableForSale price{amount} compareAtPrice{amount} selectedOptions{name value}}}}}';
    const key = (s) => String(s == null ? '' : s).trim().toLowerCase();
    const names = ((product && product.options) || []).map(o => key(this.getOptionName(o))).slice(0, 3);
    const cents = (m) => (m && m.amount != null ? Math.round(parseFloat(m.amount) * 100) : null);
    const all = [];
    let cursor = null, total = 0;
    for (let page = 0; page < 20; page++) {
      const r = await this.nfStorefront(query, { h: handle, c: cursor });
      const p = r.data && r.data.product;
      if (!p || !p.variants) return null;
      total = (p.variantsCount && Number(p.variantsCount.count)) || total;
      p.variants.nodes.forEach(v => {
        const opts = v.selectedOptions || [];
        const val = (idx) => {
          const n = names[idx];
          const hit = n ? opts.find(o => key(o.name) === n) : null;
          const o = hit || opts[idx];
          return o && o.value != null ? String(o.value) : null;
        };
        all.push({
          id: Number(String(v.id).split('/').pop()),
          title: v.title,
          option1: val(0), option2: val(1), option3: val(2),
          price: cents(v.price) || 0,
          compare_at_price: cents(v.compareAtPrice),
          available: !!v.availableForSale
        });
      });
      if (!p.variants.pageInfo || !p.variants.pageInfo.hasNextPage) break;
      cursor = p.variants.pageInfo.endCursor;
    }
    if (!all.length || (total && all.length !== total)) {
      console.warn(`[NF] Storefront API (ohne Token): ${handle} unvollständig ${all.length}/${total}`);
      return null;
    }
    const js = new Map(((product && product.variants) || []).map(v => [String(v.id), v]));   // .js-Objekte der ersten 250 behalten
    return all.map(v => js.get(String(v.id)) || v);
  }


  init() {
    this.cacheDOM();
    this.bindEvents();

    // Debug: Log subtitle loading info
    if (this.settings.debugSubtitleInfo) {
      const handles = this.settings.productHandles || {};
      const handleKeys = Object.keys(handles);
      console.log('=== SUBTITLE DEBUG INFO ===');
      console.log('Collection product count:', this.settings.collectionProductCount);
      console.log('Product handles loaded:', handleKeys.length);
      console.log('Using title parsing fallback for subtitles (extracts last part after " - ")');
    }

    // Store the promise so openModal can wait for products to load
    this.productsLoadedPromise = this.fetchCollectionProducts();

    // Check if there's a shared bundle in URL first (takes priority over localStorage)
    const hasSharedBundle = this.loadFromURL();

    // Load saved selections from localStorage only if no shared bundle
    // NF 2026-09-30: restorePromise = Wiederherstellung fertig. Die Fenster warten darauf (nf-bundle-ux.js
    // openModal) – sonst öffnet das Griffe-Fenster schon, wenn nur die Griffe geladen sind, zeigt „nichts
    // gewählt“ und die gespeicherte Auswahl überschreibt später die neue (oder die neue löscht die gespeicherte).
    // Ohne Speicher/Link ist das Promise sofort erfüllt. Darf nie abgelehnt werden.
    this.restorePromise = hasSharedBundle
      ? (this._urlRestorePromise || Promise.resolve())
      : this.loadFromLocalStorage();
  }

  cacheDOM() {
    // Modal elements (shared across all sections)
    this.modal = document.querySelector('[data-bundle-modal]');

    if (!this.modal) {
      console.error('❌ Bundle modal not found! Make sure [data-bundle-modal] element exists on the page.');
      return;
    }

    // Move modal to body to ensure it's at the root stacking context
    // This prevents headers and other fixed elements from appearing above it
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      console.log('✓ Modal moved to body for proper z-index stacking');
    }

    this.modalContent = this.modal.querySelector('.bundle-modal-content');
    this.closeModalBtns = this.modal.querySelectorAll('[data-modal-close]');
    this.confirmBtn = this.modal.querySelector('[data-confirm-selection]');

    // Selection summary elements
    this.selectedCountDisplay = this.modal.querySelector('[data-selected-count]');
    this.selectedTotalDisplay = this.modal.querySelector('[data-selected-total]');

    console.log('📦 Cached DOM elements:');
    console.log('  Modal:', this.modal);
    console.log('  Confirm button:', this.confirmBtn);
    console.log('  Close buttons:', this.closeModalBtns.length);
    this.productsGrid = this.modal.querySelector('[data-products-grid]');
    this.searchInput = this.modal.querySelector('[data-search-input]');
    this.searchContainer = this.modal.querySelector('.bundle-search-container');

    // Already selected section
    this.alreadySelectedSection = this.modal.querySelector('[data-already-selected-section]');
    this.alreadySelectedItems = this.modal.querySelector('[data-already-selected-items]');
    this.alreadySelectedCount = this.modal.querySelector('[data-already-selected-count]');
    this.alreadySelectedToggle = this.modal.querySelector('[data-already-selected-toggle]');
    this.alreadySelectedContent = this.modal.querySelector('[data-already-selected-content]');

    // Bundle Add to Cart button (outside all accordions)
    this.bundleAtcWrapper = this.scopedQuery('[data-bundle-atc-wrapper]');
    this.bundleAddToCartBtn = this.scopedQuery('[data-bundle-add-to-cart]');

    // Bundle price container (desktop)
    this.bundlePriceContainer = this.scopedQuery('[data-bundle-price-container]');
    this.bundleNowPrice = this.scopedQuery('[data-bundle-now-price]');
    this.bundleOldPrice = this.scopedQuery('[data-bundle-old-price]');

    // Bundle price container (mobile - outside floating bar)
    this.bundlePriceContainerMobile = this.scopedQuery('[data-bundle-price-container-mobile]');
    this.bundleNowPriceMobile = this.scopedQuery('[data-bundle-now-price-mobile]');
    this.bundleOldPriceMobile = this.scopedQuery('[data-bundle-old-price-mobile]');

    // Share Bundle button
    this.bundleShareBtn = this.scopedQuery('[data-bundle-share]');

    // Section-specific elements
    this.accordionTriggers = this.scopedQueryAll('[data-accordion-trigger]');
    this.openModalBtns = this.scopedQueryAll('[data-open-modal]');
  }

  bindEvents() {
    // Accordion toggles (one for each section)
    this.accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => this.toggleAccordion(trigger));
    });

    // Modal open buttons (one for each section)
    this.openModalBtns.forEach(btn => {
      console.log('🔘 Binding modal open button:', btn);
      console.log('  Button computed style:', window.getComputedStyle(btn));
      console.log('  Button display:', window.getComputedStyle(btn).display);
      console.log('  Button pointer-events:', window.getComputedStyle(btn).pointerEvents);
      console.log('  Button z-index:', window.getComputedStyle(btn).zIndex);

      btn.addEventListener('click', (e) => {
        console.log('=== BUTTON CLICKED ===');
        console.log('  Window width:', window.innerWidth);
        console.log('  Is mobile?', window.innerWidth <= 767);
        console.log('  Button:', e.target);
        console.log('  SectionId:', btn.dataset.sectionId);
        const sectionId = btn.dataset.sectionId;
        this.openModal(sectionId);
      }, { capture: false, passive: false });
    });

    // Modal close
    if (this.closeModalBtns) {
      this.closeModalBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          console.log('✓ Close button (X) clicked');
          this.closeModal();
        });
      });
    }

    if (this.confirmBtn) {
      console.log('🔘 Binding confirm button event listener');
      this.confirmBtn.addEventListener('click', (e) => {
        console.log('🔘 CONFIRM BUTTON CLICKED!');
        console.log('  Event:', e);
        console.log('  Target:', e.target);
        console.log('  CurrentTarget:', e.currentTarget);
        e.preventDefault();
        e.stopPropagation();
        console.log('✓ Confirm button clicked, calling confirmSelection...');
        this.confirmSelection();
      });
      console.log('✓ Confirm button event listener bound');
    } else {
      console.error('❌ Confirm button not found!');
    }

    // Search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    }

    // Color filter will be bound dynamically after colors are extracted

    // Bundle Add to Cart button
    if (this.bundleAddToCartBtn) {
      this.bundleAddToCartBtn.addEventListener('click', () => this.handleBundleAddToCart());
    }

    // Share Bundle button
    if (this.bundleShareBtn) {
      this.bundleShareBtn.addEventListener('click', () => this.handleShareBundle());
    }

    // Prevent modal close when clicking inside content
    this.modalContent.addEventListener('click', (e) => e.stopPropagation());

    // Intercept main product add to cart
    this.interceptAddToCart();

    // Already selected section toggle
    if (this.alreadySelectedToggle) {
      this.alreadySelectedToggle.addEventListener('click', () => this.toggleAlreadySelectedSection());
      // Make the whole header clickable
      const header = this.alreadySelectedToggle.closest('.bundle-already-selected-header');
      if (header) {
        header.addEventListener('click', (e) => {
          if (e.target !== this.alreadySelectedToggle && !this.alreadySelectedToggle.contains(e.target)) {
            this.toggleAlreadySelectedSection();
          }
        });
      }
    }

    // Preview accordion toggle
    const previewToggle = this.modal.querySelector('[data-preview-toggle]');
    if (previewToggle) {
      previewToggle.addEventListener('click', () => this.togglePreviewAccordion());
    }

    // Listen for main product variant changes to sync colors with bundle products
    this.bindMainProductVariantListener();
  }

  bindMainProductVariantListener() {
    // Listen to variant changes on the main product page
    // This allows syncing the selected color/surface to bundle products

    // Debounce timer to prevent multiple rapid calls
    let variantChangeTimer = null;
    const handleVariantChange = () => {
      // Clear any pending timer to debounce rapid changes
      if (variantChangeTimer) {
        clearTimeout(variantChangeTimer);
      }
      // Use a longer delay to let the theme's JS complete its work first
      variantChangeTimer = setTimeout(() => {
        console.log('🔄 Main product variant changed, re-detecting color and background...');
        this.detectAndSetMainProductColor();
        this.detectAndSetMainProductBackground();
        // If modal is open, refresh the products grid to show the new color selection
        if (this.modal && this.modal.classList.contains('is-open')) {
          // Clear toggled variants cache so products refresh with new color
          this.lastToggledVariantByProduct = {};
          this.renderProductsGrid(this.currentSearchTerm);
        }
      }, 300);
    };

    // Track elements that already have listeners to prevent duplicates
    const elementsWithListeners = new WeakSet();

    // Method 1: Listen to all potential variant selectors
    const variantSelectors = [
      'input[type="radio"][name*="option"]',
      'select[name*="option"]',
      'input[type="radio"][data-option]',
      'select[data-option]',
      '.variant-input input[type="radio"]',
      '.product-form__input input[type="radio"]',
      'variant-radios input[type="radio"]',
      'variant-selects select',
      // Swatch-based themes use name="id" for variant selection
      'input[type="radio"][name="id"]',
      '.swatch-item input[type="radio"]'
    ];

    variantSelectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(element => {
          // Skip if we already added a listener to this element
          if (elementsWithListeners.has(element)) {
            return;
          }
          elementsWithListeners.add(element);
          element.addEventListener('change', handleVariantChange);
        });
      } catch (e) {
        // Invalid selector, skip
      }
    });

    // Method 2: Use MutationObserver to detect URL changes (variant changes often update URL)
    let lastVariantParam = new URLSearchParams(window.location.search).get('variant');

    // Check URL periodically for variant changes (some themes update URL without events)
    setInterval(() => {
      const currentVariantParam = new URLSearchParams(window.location.search).get('variant');
      if (currentVariantParam !== lastVariantParam) {
        lastVariantParam = currentVariantParam;
        console.log('🔄 URL variant changed, re-detecting color and background...');
        this.detectAndSetMainProductColor();
        this.detectAndSetMainProductBackground();
        // If modal is open, refresh the products grid
        if (this.modal && this.modal.classList.contains('is-open')) {
          this.lastToggledVariantByProduct = {};
          this.renderProductsGrid(this.currentSearchTerm);
        }
      }
    }, 500);

    console.log('✅ Main product variant listener bound');
  }

  togglePreviewAccordion() {
    const toggle = this.modal.querySelector('[data-preview-toggle]');
    const content = this.modal.querySelector('[data-preview-content]');

    if (!toggle || !content) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      content.classList.add('collapsed');
    } else {
      content.classList.remove('collapsed');
    }
  }

  toggleAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', !isExpanded);
  }

  async openModal(sectionId) {
    console.log('=== OPEN MODAL CALLED ===');
    console.log('  SectionId:', sectionId);
    console.log('  Modal element:', this.modal);
    console.log('  Current modal display:', this.modal ? this.modal.style.display : 'N/A');

    this.currentSectionId = sectionId;

    // Find the section configuration
    const sectionConfig = this.sections.find(s => s.sectionId === sectionId);
    if (!sectionConfig) {
      console.error('Section configuration not found for:', sectionId);
      return;
    }

    // Mark as fresh open - selections won't show until user interacts
    this.isModalFreshOpen = true;

    console.log('  Setting modal display to flex...');
    console.log('  Window width:', window.innerWidth);
    console.log('  Is desktop?', window.innerWidth > 767);

    // Force modal to show with multiple methods for maximum compatibility
    // Add is-open class for proper z-index and visibility
    this.modal.classList.add('is-open');

    // iOS Safari scroll lock: Save scroll position (for potential restore on close)
    this.savedScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;

    // Add class to body to force headers behind modal
    document.body.classList.add('bundle-modal-open');

    // iOS Safari: Lock scroll on html element too
    document.documentElement.classList.add('bundle-modal-open');

    // Dynamically lower z-index of ALL fixed/sticky elements (except our modal)
    this.lowerFixedElementsZIndex();

    this.modal.style.setProperty('display', 'flex', 'important');
    this.modal.style.setProperty('z-index', '2147483647', 'important');
    this.modal.style.setProperty('position', 'fixed', 'important');
    this.modal.style.setProperty('top', '0', 'important');
    this.modal.style.setProperty('left', '0', 'important');
    this.modal.style.setProperty('right', '0', 'important');
    this.modal.style.setProperty('bottom', '0', 'important');
    this.modal.style.visibility = 'visible';
    this.modal.style.opacity = '1';

    console.log('  Modal display after setting:', this.modal.style.display);
    console.log('  Modal computed style:', window.getComputedStyle(this.modal).display);
    console.log('  Modal classList:', this.modal.classList.toString());
    console.log('  Modal visibility:', window.getComputedStyle(this.modal).visibility);
    console.log('  Modal opacity:', window.getComputedStyle(this.modal).opacity);
    document.body.style.overflow = 'hidden';
    if (this.searchInput) {
      this.searchInput.value = '';
    }

    // Show loading state while products load
    if (this.productsGrid) {
      this.productsGrid.innerHTML = '<div class="bundle-loading-state">Produkte werden geladen...</div>';
    }

    // Wait for products to be loaded before proceeding
    console.log('⏳ Waiting for products to load...');
    await this.productsLoadedPromise;
    console.log('✅ Products loaded, continuing with modal setup');

    console.log('=== BUNDLE MODAL OPENED ===');
    console.log('Section:', sectionConfig.sectionTitle);
    console.log('Metafield filters:', {
      element: sectionConfig.filterElement,
      style: sectionConfig.filterStyle,
      surface: sectionConfig.filterSurface
    });

    // Clear cached variant selections so color filter can work on fresh open
    // This ensures that when user changes main product color and reopens popup,
    // the bundle products reflect the new color selection
    this.lastToggledVariantByProduct = {};

    // Auto-detect and set color filter from main product
    this.detectAndSetMainProductColor();
    // Auto-detect and set background color filter (Hintergrund Farbe der Griffe)
    this.detectAndSetMainProductBackground();
    // Re-evaluate the colour dropdown for the section being opened
    this.renderColorFilter();


    // Filter products for this section
    this.filterProductsForSection(sectionConfig);

    // Render already selected items for this section
    this.renderAlreadySelectedItems();

    // Update the selection summary in footer
    this.updateSelectionSummary();

    this.renderProductsGrid();

    // On mobile, collapse the "Ausgewählte Produkte" accordion by default
    if (window.innerWidth <= 767) {
      const toggle = this.modal.querySelector('[data-preview-toggle]');
      const content = this.modal.querySelector('[data-preview-content]');
      if (toggle && content) {
        toggle.setAttribute('aria-expanded', 'false');
        content.classList.add('collapsed');
      }
    }
  }

  toggleAlreadySelectedSection() {
    const isExpanded = this.alreadySelectedToggle.getAttribute('aria-expanded') === 'true';
    this.alreadySelectedToggle.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      this.alreadySelectedContent.classList.add('collapsed');
    } else {
      this.alreadySelectedContent.classList.remove('collapsed');
    }
  }

  renderAlreadySelectedItems() {
    console.log('📋 renderAlreadySelectedItems() called');
    console.log('  Current section:', this.currentSectionId);
    console.log('  Total selected products:', this.selectedProducts.size);
    console.log('  All selectedProducts Map entries:');
    this.selectedProducts.forEach((value, key) => {
      console.log(`    Key: "${key}" -> Variant ID: ${value.selectedVariant.id}, Product: ${value.product.title}`);
    });

    // Get products selected for the current section
    const sectionSelections = Array.from(this.selectedProducts.values()).filter(
      item => item.sectionId === this.currentSectionId
    );
    console.log('  Items for this section:', sectionSelections.length);
    sectionSelections.forEach((item, idx) => {
      console.log(`    [${idx}] Product: ${item.product.title}, Variant ID: ${item.selectedVariant.id}, Section: ${item.sectionId}`);
    });

    if (sectionSelections.length === 0) {
      // Hide the section if nothing is selected
      if (this.alreadySelectedSection) {
        this.alreadySelectedSection.style.display = 'none';
      }
      return;
    }

    // Show and populate the section
    if (this.alreadySelectedSection) {
      this.alreadySelectedSection.style.display = 'block';
    }

    // Update count
    if (this.alreadySelectedCount) {
      this.alreadySelectedCount.textContent = `(${sectionSelections.length})`;
    }

    // Render items
    if (this.alreadySelectedItems) {
      this.alreadySelectedItems.innerHTML = sectionSelections.map(item => {
        const { product, selectedVariant, quantity, sectionId } = item;
        const variantTitle = this.getVariantTitle(selectedVariant, product);
        const price = this.formatMoney(selectedVariant.price);
        const image = this.getProductImage(product);

        const displayTitle = this.getProductDisplayTitle(product);
        return `
          <div class="bundle-already-selected-item" data-variant-id="${selectedVariant.id}">
            <img src="${image}" alt="${displayTitle}" class="bundle-already-selected-item-image" />
            <div class="bundle-already-selected-item-details">
              <div class="bundle-already-selected-item-title">${displayTitle}</div>
              ${variantTitle ? `<div class="bundle-already-selected-item-variant">${variantTitle}</div>` : ''}
              <div class="bundle-already-selected-item-price">
                ${price}
                <span class="bundle-already-selected-item-qty">× ${quantity}</span>
              </div>
            </div>
            <button
              type="button"
              class="bundle-already-selected-item-remove"
              data-remove-variant-id="${selectedVariant.id}"
              data-remove-section-id="${sectionId}"
              aria-label="Remove ${displayTitle}"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `;
      }).join('');

      // Bind remove button events
      const removeButtons = this.alreadySelectedItems.querySelectorAll('[data-remove-variant-id]');
      console.log('🗑️ BINDING REMOVE BUTTONS IN ALREADY SELECTED SECTION');
      console.log(`  Found ${removeButtons.length} remove buttons to bind`);

      removeButtons.forEach((btn, index) => {
        const variantId = btn.dataset.removeVariantId;
        const sectionId = btn.dataset.removeSectionId;
        console.log(`  Button ${index}: variant ID = "${variantId}", section ID = "${sectionId}"`);

        btn.addEventListener('click', (e) => {
          console.log('🗑️ REMOVE BUTTON CLICKED IN "ALREADY IN BUNDLE"!');
          console.log('  Button index:', index);
          console.log('  Variant ID from button:', variantId);
          console.log('  Section ID from button:', sectionId);
          console.log('  Compound key:', `${sectionId}_${variantId}`);
          console.log('  Does this key exist in Map?', this.selectedProducts.has(`${sectionId}_${variantId}`));
          console.log('  All Map keys:', Array.from(this.selectedProducts.keys()));

          e.preventDefault();
          e.stopPropagation();

          this.removeSelectedProduct(variantId, sectionId);
        });
        console.log(`  ✓ Bound click event to button ${index}`);
      });
    }
  }

  updateSelectionSummary() {
    console.log('💰 updateSelectionSummary() called');
    console.log('  Current section:', this.currentSectionId);

    // Always update the preview first, even if summary elements aren't found
    this.renderSelectedPreview();

    if (!this.selectedCountDisplay || !this.selectedTotalDisplay) {
      console.warn('Summary elements not found');
      return;
    }

    // Calculate total count and price for the CURRENT SECTION only (shown in modal footer)
    let totalCount = 0;
    let totalPrice = 0;

    this.selectedProducts.forEach((item) => {
      // Only count items from the current section
      if (item.sectionId === this.currentSectionId) {
        const quantity = item.quantity || 1;
        totalCount += quantity;

        // Get price in cents
        const variantPrice = parseInt(item.selectedVariant.price) || 0;
        totalPrice += variantPrice * quantity;
      }
    });

    console.log(`  Items for section ${this.currentSectionId}: ${totalCount}`);
    console.log(`  Total price (cents): ${totalPrice}`);

    // Update displays
    this.selectedCountDisplay.textContent = totalCount;
    this.selectedTotalDisplay.textContent = this.formatMoney(totalPrice);

    // Update section header summaries
    this.updateSectionHeaderSummaries();
  }

  updateSectionHeaderSummaries() {
    // Calculate per-section totals
    const sectionTotals = {};

    this.selectedProducts.forEach((item) => {
      const sectionId = item.sectionId;
      if (!sectionTotals[sectionId]) {
        sectionTotals[sectionId] = { count: 0, total: 0 };
      }

      const quantity = item.quantity || 1;
      const variantPrice = parseInt(item.selectedVariant.price) || 0;

      sectionTotals[sectionId].count += quantity;
      sectionTotals[sectionId].total += variantPrice * quantity;
    });

    // Update each section's header display.
    // Was hardcoded to section1..3, so a section configured on slot 4 or 5
    // (METOD keeps Griffe on section4) never got its count and total updated -
    // the selection was stored and rendered, but the header stayed at 0.
    const sectionIds = (this.sections || []).map(s => s.sectionId).filter(Boolean);
    (sectionIds.length ? sectionIds : ['section1', 'section2', 'section3']).forEach(sectionId => {
      const countEl = this.scopedQuery(`[data-section-count="${sectionId}"]`);
      const totalEl = this.scopedQuery(`[data-section-total="${sectionId}"]`);

      if (countEl && totalEl) {
        const data = sectionTotals[sectionId] || { count: 0, total: 0 };
        countEl.textContent = data.count;
        totalEl.textContent = this.formatMoney(data.total);
      }
    });
  }

  renderSelectedPreview() {
    console.log('=== RENDER SELECTED PREVIEW ===');
    console.log('this.modal:', this.modal);
    console.log('selectedProducts size:', this.selectedProducts.size);
    console.log('currentSectionId:', this.currentSectionId);

    if (!this.modal) {
      console.warn('Modal not initialized, cannot render preview');
      return;
    }

    const previewContainer = this.modal.querySelector('[data-selected-preview-container]');
    const previewScroll = this.modal.querySelector('[data-selected-preview-scroll]');

    console.log('previewContainer:', previewContainer);
    console.log('previewScroll:', previewScroll);

    if (!previewContainer || !previewScroll) {
      console.warn('Preview container not found');
      return;
    }

    // Filter to only show products from the current section
    const selectedArray = Array.from(this.selectedProducts.values()).filter(
      item => item.sectionId === this.currentSectionId
    );
    console.log('Selected products for current section:', selectedArray.length);

    if (selectedArray.length === 0) {
      previewContainer.classList.remove('has-items');
      previewScroll.innerHTML = '';
      return;
    }

    previewContainer.classList.add('has-items');
    console.log('Added has-items class to previewContainer');
    console.log('previewContainer classes:', previewContainer.className);

    previewScroll.innerHTML = selectedArray.map(item => {
      const { product, selectedVariant, quantity } = item;
      const image = this.getProductImage(product);
      const key = `${selectedVariant.id}_${item.sectionId}`;
      const subtitle = this.getProductDisplayTitle(product);
      const qty = quantity || 1;
      const variantTitle = this.getVariantTitle(selectedVariant, product);

      return `
        <div class="bundle-selected-preview-item" data-preview-key="${key}">
          <div class="preview-image-wrapper">
            <img src="${image}" alt="${subtitle}" />
            ${qty > 1 ? `<span class="preview-quantity-badge">${qty}x</span>` : ''}
            <button type="button" class="preview-remove" data-preview-remove="${key}" aria-label="Remove">×</button>
          </div>
          <span class="preview-subtitle">${subtitle}</span>
          ${variantTitle ? `<span class="preview-variant">${variantTitle}</span>` : ''}
        </div>
      `;
    }).join('');

    // Bind remove events
    previewScroll.querySelectorAll('[data-preview-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const key = btn.dataset.previewRemove;
        // Key is variantId_sectionId
        const parts = key.split('_');
        const variantId = parts[0];
        const sectionId = parts.slice(1).join('_'); // In case sectionId has underscores
        this.removeSelectedProduct(variantId, sectionId);
      });
    });
  }

  filterProductsForSection(sectionConfig) {
    // A section may draw from its own collection (e.g. handles); otherwise it
    // uses the block's main collection as before.
    const pool = this.sectionUsesOwnCollection(sectionConfig)
      ? ((this.sectionCollectionProducts || {})[sectionConfig.collectionHandle] || [])
      : this.allCollectionProducts;
    let products = [...pool];

    // NF: Griffe je Stil (z. B. Liniert -> nur griffe-linea)
    if (this.styleRuleAppliesToSection(sectionConfig)) {
      products = products.filter(p => this.isHandleAllowedByStyleRule(p));
    }

    // Apply title filters if specified for this section
    const hasFilters = (sectionConfig.filterElement && sectionConfig.filterElement.trim()) ||
                       (sectionConfig.filterStyle && sectionConfig.filterStyle.trim()) ||
                       (sectionConfig.filterSurface && sectionConfig.filterSurface.trim());

    if (hasFilters) {
      const filterElement = sectionConfig.filterElement ? sectionConfig.filterElement.toLowerCase().trim() : '';
      const filterStyle = sectionConfig.filterStyle ? sectionConfig.filterStyle.toLowerCase().trim() : '';
      const filterSurface = sectionConfig.filterSurface ? sectionConfig.filterSurface.toLowerCase().trim() : '';

      // Support comma-separated values for element filter (OR logic)
      const elementFilters = filterElement ? filterElement.split(',').map(f => f.trim()).filter(f => f) : [];

      console.log('Filtering products by title containing:', { elementFilters, filterStyle, filterSurface });

      products = products.filter(p => {
        const title = p.title.toLowerCase();

        // Element filter: match ANY of the comma-separated values (OR logic)
        if (elementFilters.length > 0) {
          const matchesAnyElement = elementFilters.some(ef => title.includes(ef));
          if (!matchesAnyElement) {
            return false;
          }
        }

        if (filterStyle && !title.includes(filterStyle)) {
          return false;
        }

        if (filterSurface && !title.includes(filterSurface)) {
          return false;
        }

        return true;
      });

      console.log(`Filtered ${pool.length} products to ${products.length} by title`);
    }

    this.availableProducts = products;
  }

  closeModal() {
    console.log('📴 Closing modal...');

    this.modal.classList.remove('is-open');
    // Remove class from body and html to restore scroll
    document.body.classList.remove('bundle-modal-open');
    document.documentElement.classList.remove('bundle-modal-open');

    // Restore z-index of all fixed/sticky elements
    this.restoreFixedElementsZIndex();

    this.modal.style.setProperty('display', 'none', 'important');
    this.modal.style.visibility = 'hidden';
    this.modal.style.opacity = '0';
    document.body.style.overflow = '';

    console.log('✓ Modal closed successfully');
  }

  // Store original z-index values and lower all fixed/sticky elements
  lowerFixedElementsZIndex() {
    this.originalZIndexes = new Map();
    this.originalPositions = new Map();

    console.log('🔧 Starting z-index fix...');

    // CRITICAL: Target #root specifically - this is the main stacking context issue
    const root = document.getElementById('root');
    if (root) {
      this.originalZIndexes.set(root, root.style.zIndex);
      this.originalPositions.set(root, root.style.position);
      root.style.setProperty('z-index', 'auto', 'important');
      root.style.setProperty('position', 'static', 'important');
      console.log('📉 Reset #root z-index and position to auto/static');
    }

    // Target all Shopify section headers
    document.querySelectorAll('.shopify-section-header, [class*="shopify-section-group-header"]').forEach(el => {
      if (!this.originalZIndexes.has(el)) {
        this.originalZIndexes.set(el, el.style.zIndex);
        this.originalPositions.set(el, el.style.position);
        el.style.setProperty('z-index', 'auto', 'important');
        console.log('📉 Reset shopify section header z-index');
      }
    });

    // Target #header and related elements directly
    ['#header', '#header-outer', '#header-inner', '#nav', '#nav-bar', '#nav-top'].forEach(selector => {
      const el = document.querySelector(selector);
      if (el && !this.originalZIndexes.has(el)) {
        this.originalZIndexes.set(el, el.style.zIndex);
        this.originalPositions.set(el, el.style.position);
        el.style.setProperty('z-index', 'auto', 'important');
        console.log('📉 Reset', selector, 'z-index');
      }
    });

    // Find ALL elements with position fixed/sticky and lower their z-index
    const allElements = document.querySelectorAll('*');
    let count = 0;

    allElements.forEach(el => {
      // Skip our bundle modal elements
      if (el.closest('.bundle-modal') || el.classList.contains('bundle-modal') ||
          el.classList.contains('bundle-modal-overlay') || el.classList.contains('bundle-modal-content')) {
        return;
      }

      const computedStyle = window.getComputedStyle(el);
      const position = computedStyle.position;
      const zIndex = computedStyle.zIndex;
      const zIndexNum = parseInt(zIndex, 10);

      // Target ANY element with position fixed/sticky OR high z-index
      if ((position === 'fixed' || position === 'sticky') || (zIndex !== 'auto' && zIndexNum > 10)) {
        if (!this.originalZIndexes.has(el)) {
          this.originalZIndexes.set(el, el.style.zIndex);
          el.style.setProperty('z-index', '1', 'important');
          count++;
        }
      }
    });

    console.log(`📉 Lowered z-index of ${count} additional elements`);
    console.log(`📉 Total elements modified: ${this.originalZIndexes.size}`);
  }

  // Restore original z-index values
  restoreFixedElementsZIndex() {
    if (!this.originalZIndexes) return;

    this.originalZIndexes.forEach((originalValue, el) => {
      if (originalValue) {
        el.style.zIndex = originalValue;
      } else {
        el.style.removeProperty('z-index');
      }
    });

    // Restore positions
    if (this.originalPositions) {
      this.originalPositions.forEach((originalValue, el) => {
        if (originalValue) {
          el.style.position = originalValue;
        } else {
          el.style.removeProperty('position');
        }
      });
      this.originalPositions.clear();
    }

    console.log(`📈 Restored z-index of ${this.originalZIndexes.size} elements`);
    this.originalZIndexes.clear();
  }

  /** True when this section draws from a collection of its own. */
  sectionUsesOwnCollection(sectionConfig) {
    const h = sectionConfig && sectionConfig.collectionHandle;
    return Boolean(h) && h !== this.settings.collectionHandle;
  }

  /** True while the open modal belongs to a section with its own collection. */
  currentSectionUsesOwnCollection() {
    if (!this.currentSectionId) return false;
    const cfg = (this.settings.sections || []).find(s => s.sectionId === this.currentSectionId);
    return this.sectionUsesOwnCollection(cfg);
  }

  /* ------------------------------------------------------------------ */
  /* NF 2026-09-29: Griffe je Stil einschränken                          */
  /*  Block-Einstellung griffe_style_rules, z. B. "Liniert: griffe-linea" */
  /*  -> auf Liniert-Konfiguratoren bietet der Griffe-Bereich nur Linea  */
  /*  an. Gilt für Bereiche mit eigener Collection, deren Titel "Griff"   */
  /*  enthält oder deren Collection einen der genannten Griffe führt.     */
  /*  Auswahl aus Speicher, geteiltem Link oder "Bearbeiten" wird beim    */
  /*  Laden bereinigt; der Warenkorb-Aufruf prüft noch einmal.            */
  /* ------------------------------------------------------------------ */

  normalizeStyleName(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /** Active rule for this product's style: { style, handles[] } or null. */
  getStyleRule() {
    if (this._styleRule !== undefined) return this._styleRule;
    const raw = String(this.settings.productStyle || '');
    // A list metafield arrives as "A, B" – any of the product's styles may match.
    const styles = raw.split(',').map(s => s.trim()).filter(Boolean);
    const rules = Array.isArray(this.settings.griffeStyleRules) ? this.settings.griffeStyleRules : [];
    let hit = null;
    for (const r of rules) {
      const want = this.normalizeStyleName(r && r.style);
      const handles = ((r && r.handles) || []).map(h => String(h).toLowerCase().trim()).filter(Boolean);
      if (!want || !handles.length) continue;
      const own = styles.find(s => this.normalizeStyleName(s) === want);
      if (own) { hit = { style: own, handles }; break; }
    }
    this._styleRule = hit;
    return hit;
  }

  styleRuleAppliesToSection(sectionConfig) {
    const rule = this.getStyleRule();
    if (!rule || !sectionConfig || !this.sectionUsesOwnCollection(sectionConfig)) return false;
    if (/griff/i.test(sectionConfig.sectionTitle || '')) return true;
    const pool = (this.sectionCollectionProducts || {})[sectionConfig.collectionHandle] || [];
    return pool.some(p => rule.handles.includes(String(p.handle || '').toLowerCase()));
  }

  isHandleAllowedByStyleRule(product) {
    const rule = this.getStyleRule();
    if (!rule) return true;
    return rule.handles.includes(String((product && product.handle) || '').toLowerCase());
  }

  /** True if this product must not be part of the composition on this page. */
  isBlockedByStyleRule(product, sectionId) {
    if (!product || this.isHandleAllowedByStyleRule(product)) return false;
    const governed = (this.sections || []).filter(c => this.styleRuleAppliesToSection(c));
    if (!governed.length) return false;
    if (governed.some(c => c.sectionId === sectionId)) return true;
    // Also catches a handle filed under another section (hand-edited share link)
    const id = String(product.id);
    return governed.some(c => ((this.sectionCollectionProducts || {})[c.collectionHandle] || [])
      .some(p => String(p.id) === id));
  }

  /** Model name of a handle product: "Fronten Griffe - Linea - 40 x 41 mm" -> "Linea". */
  styleRuleProductName(product) {
    const parts = String((product && product.title) || '').split(' - ').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) return parts[1];
    return this.styleRuleHandleName((product && product.handle) || '');
  }

  styleRuleHandleName(handle) {
    const base = String(handle || '').replace(/^(fronten-)?griffe?-/, '').replace(/-/g, ' ').trim();
    return base ? base.charAt(0).toUpperCase() + base.slice(1) : String(handle || '');
  }

  /** Names of the allowed handles ("Linea"), from loaded products where possible. */
  styleRuleAllowedNames() {
    const rule = this.getStyleRule();
    if (!rule) return [];
    const loaded = this.getAllLoadedProducts ? this.getAllLoadedProducts() : [];
    const names = [];
    rule.handles.forEach(h => {
      const p = loaded.find(x => String(x.handle || '').toLowerCase() === h);
      const n = p ? this.styleRuleProductName(p) : this.styleRuleHandleName(h);
      if (n && !names.includes(n)) names.push(n);
    });
    return names;
  }

  joinNamesGerman(names) {
    if (names.length <= 1) return names[0] || '';
    return names.slice(0, -1).join(', ') + ' oder ' + names[names.length - 1];
  }

  /** Hint line for the handles step, e.g. "Für Liniert-Fronten passt der Griff Linea." */
  getStyleRuleHint() {
    const rule = this.getStyleRule();
    if (!rule) return '';
    const tpl = String(this.settings.griffeStyleHint || 'Für {stil}-Fronten passt der Griff {griffe}.');
    return tpl.replace(/\{stil\}/g, rule.style).replace(/\{griffe\}/g, this.joinNamesGerman(this.styleRuleAllowedNames()));
  }

  /**
   * Removes every selection the rule does not allow (restored draft, shared
   * link, "Bearbeiten", localStorage). Returns the removed entries and shows a
   * short notice unless quiet.
   */
  enforceStyleRule(quiet) {
    if (!this.getStyleRule() || !this.selectedProducts || !this.selectedProducts.size) return [];
    const removed = [];
    Array.from(this.selectedProducts.entries()).forEach(([key, it]) => {
      if (it && this.isBlockedByStyleRule(it.product, it.sectionId)) {
        removed.push(it);
        this.selectedProducts.delete(key);
        if (this.userModifiedVariants) this.userModifiedVariants.delete(key);
      }
    });
    if (removed.length && !quiet) this.showNotification(this.styleRuleRemovedMessage(removed), 6000);
    return removed;
  }

  /** Redraw lists, headers, totals and storage after enforceStyleRule() removed something. */
  refreshAfterStyleRule() {
    (this.sections || []).forEach(section => this.renderSelectedProductsForSection(section.sectionId));
    this.updateSectionHeaderSummaries();
    this.updateBundleAddToCartButton();
    this.saveToLocalStorage();
  }

  styleRuleRemovedMessage(removed) {
    const rule = this.getStyleRule();
    const names = [];
    removed.forEach(it => {
      const n = this.styleRuleProductName(it.product);
      if (n && !names.includes(n)) names.push(n);
    });
    const list = names.length > 1 ? names.slice(0, -1).join(', ') + ' und ' + names[names.length - 1] : (names[0] || '');
    const lead = names.length > 1
      ? `Die Griffe ${list} passen nicht zu ${rule.style}-Fronten und wurden aus der Auswahl entfernt.`
      : `Der Griff ${list} passt nicht zu ${rule.style}-Fronten und wurde aus der Auswahl entfernt.`;
    const allowedNames = this.styleRuleAllowedNames();
    if (!allowedNames.length) return lead;
    return allowedNames.length > 1
      ? `${lead} Passend sind ${allowedNames.slice(0, -1).join(', ')} und ${allowedNames[allowedNames.length - 1]}.`
      : `${lead} Passend ist ${allowedNames[0]}.`;
  }

  async fetchCollectionProducts() {
    if (!this.settings.collectionHandle) {
      console.error('No collection specified');
      return;
    }

    // NF 2026-09-30: Alle Sammlungen PARALLEL laden, jede mit eigenem Promise (poolPromiseFor) – das Griffe-Fenster
    // wartet nur auf die Griffe, das Fronten-Fenster nur auf die Fronten. Vorher lief die Griffe-Sammlung erst nach
    // der kompletten Fronten-Sammlung los (Desktop ~11 s bis zum Inhalt).
    // Nichts hier darf productsLoadedPromise verwerfen (ein abgelehntes Promise ließe jedes Fenster ewig warten).
    const extraHandles = [...new Set(
      (this.settings.sections || [])
        .map(s => s.collectionHandle)
        .filter(h => h && h !== this.settings.collectionHandle)
    )];
    this.sectionCollectionProducts = this.sectionCollectionProducts || {};
    this.sectionCollectionProductIds = this.sectionCollectionProductIds || new Set();
    this.poolPromises = {};

    const mainPromise = (async () => {
      try {
        const products = await this.loadProductsFromCollection(this.settings.collectionHandle, {
          main: true,
          titleFilter: this.mainSectionTitlePredicate()
        });
        this.allCollectionProducts = products;
        this.availableProducts = products;

        // Extract available colors from variant metafields (main collection only -
        // a section collection like handles has its own, unrelated finish names).
        await this.extractAvailableColors(products);
      } catch (error) {
        console.error('Error preparing main collection:', error);
        this.allCollectionProducts = [];
        this.availableProducts = [];
      }
    })();
    this.poolPromises.__main = mainPromise;

    const extraPromises = extraHandles.map(handle => {
      const promise = (async () => {
        try {
          const list = await this.loadProductsFromCollection(handle);
          this.sectionCollectionProducts[handle] = list;
          list.forEach(p => this.sectionCollectionProductIds.add(String(p.id)));
        } catch (error) {
          console.error(`Error preparing section collection "${handle}":`, error);
          this.sectionCollectionProducts[handle] = [];
        }
      })();
      this.poolPromises[handle] = promise;
      return promise;
    });

    await Promise.all([mainPromise, ...extraPromises]);
    this.nfShowLoadProblemNotice();
  }

  /**
   * NF 2026-10-23: sichtbarer Hinweis statt still zu kürzen – wenn Größen (Varianten über 250) oder ganze Fronten nicht
   * geladen werden konnten oder die Fronten-Liste leer blieb. Einmal je Seite.
   */
  nfShowLoadProblemNotice() {
    const truncated = Array.from(this._nfTruncated || []);
    const failed = Array.from(this._nfLoadFailed || []);
    const empty = !!this._nfMainListFailed;
    if ((!truncated.length && !failed.length && !empty) || this._nfLoadNoticeShown) return;
    this._nfLoadNoticeShown = true;
    console.warn('[NF] Nicht vollständig geladen:', { truncated, failed, empty });
    this.showNotification('Einige Größen laden gerade nicht – bitte neu laden oder anrufen: 0211 54208980.', 30000, {
      error: true,
      tel: { text: '0211 54208980', href: '+4921154208980' },
      action: { label: 'Neu laden', onClick: () => location.reload() }
    });
  }

  /** NF: Promise, das nur auf die Sammlung wartet, aus der dieser Bereich seine Produkte bezieht. */
  poolPromiseFor(sectionConfig) {
    const pools = this.poolPromises || {};
    if (this.sectionUsesOwnCollection(sectionConfig)) {
      return pools[sectionConfig.collectionHandle] || this.productsLoadedPromise;
    }
    return pools.__main || this.productsLoadedPromise;
  }

  /**
   * NF: Titel-Filter aller Bereiche, die die Hauptsammlung nutzen – exakt dieselbe Logik wie
   * filterProductsForSection (Element ODER-verknüpft, Stil, Oberfläche als Teiltext im Titel).
   * Produkte, die keinen Bereich erfüllen, kann das Fenster nie zeigen – sie werden gar nicht erst einzeln geladen.
   * null = nicht vorfiltern (ein Bereich ohne Filter zeigt alle Produkte).
   */
  mainSectionTitlePredicate() {
    const mains = (this.settings.sections || []).filter(s => s && !this.sectionUsesOwnCollection(s));
    if (!mains.length) return null;
    const rules = [];
    for (const sc of mains) {
      const el = sc.filterElement ? sc.filterElement.toLowerCase().trim() : '';
      const st = sc.filterStyle ? sc.filterStyle.toLowerCase().trim() : '';
      const su = sc.filterSurface ? sc.filterSurface.toLowerCase().trim() : '';
      if (!el && !st && !su) return null;
      rules.push({ els: el ? el.split(',').map(f => f.trim()).filter(Boolean) : [], st, su });
    }
    return (title) => {
      const t = String(title || '').toLowerCase();
      return rules.some(r =>
        (!r.els.length || r.els.some(ef => t.includes(ef))) &&
        (!r.st || t.includes(r.st)) &&
        (!r.su || t.includes(r.su)));
    };
  }

  /**
   * NF: schlanke Produktliste (ID, Handle, Titel) über die Storefront API – ~4 KB statt ~9 MB products.json.
   * NF 2026-10-23: OHNE Token (nfStorefront, Shopify „tokenless access“) statt mit dem Token aus dem Seitenquelltext (seit
   * 22.10. 401); ~0,2 s. Eine eigene Listen-Vorlage (?view=…) brauchte ungecacht 4–5 s, products.json ~3,7 s.
   * Gibt null zurück, wenn die Abfrage scheitert oder die Anzahl nicht zur Sammlung passt (dann greift products.json).
   */
  async listCollectionViaStorefront(collectionHandle, expectedCount) {
    const query = 'query($h:String!,$c:String){collection(handle:$h){products(first:250,after:$c){pageInfo{hasNextPage endCursor} nodes{id handle title}}}}';
    const list = [];
    let cursor = null;
    for (let page = 0; page < 10; page++) {
      const r = await this.nfStorefront(query, { h: collectionHandle, c: cursor });
      const conn = r.data && r.data.collection && r.data.collection.products;
      if (!conn) {
        console.warn(`[NF] Storefront-Liste fehlgeschlagen (${r.status}), nutze products.json`);
        return null;
      }
      conn.nodes.forEach(n => {
        const id = Number(String(n.id).split('/').pop());
        if (id && n.handle) list.push({ id, handle: n.handle, title: n.title || '' });
      });
      if (!conn.pageInfo || !conn.pageInfo.hasNextPage) break;
      cursor = conn.pageInfo.endCursor;
    }
    const want = Number(expectedCount) || 0;
    if (!list.length || (want && list.length < want)) {
      console.warn(`[NF] Storefront-Liste unvollständig (${list.length}/${want}), nutze products.json`);
      return null;
    }
    return list;
  }

  /** Every loaded product, main collection plus all section collections. */
  getAllLoadedProducts() {
    const pools = [this.allCollectionProducts || []];
    const extra = this.sectionCollectionProducts || {};
    for (const handle of Object.keys(extra)) pools.push(extra[handle] || []);
    return pools.flat();
  }

  async loadProductsFromCollection(collectionHandle, opts = {}) {
    if (!collectionHandle) return [];

    try {
      console.log('Fetching products from collection:', collectionHandle);

      // Step 1: Get ALL products from collection with pagination (250 per page max)
      // NF 2026-09-30: Hauptsammlung zuerst über die schlanke Storefront-Liste (nur ID/Handle/Titel, seit 2026-10-23
      // ohne Token); products.json liefert für 203 Fronten ~9 MB samt aller Varianten und brauchte allein ~4 s.
      let collectionProducts = [];
      let page = 1;
      let hasMoreProducts = true;
      if (opts.main) {
        const slim = await this.listCollectionViaStorefront(collectionHandle, this.settings.collectionProductCount);
        if (slim) {
          collectionProducts = slim;
          hasMoreProducts = false;
          // NF 2026-10-23: Titel je ID – benennt Positionen aus Teilen-/Bearbeiten-Links, die nicht geladen wurden
          this._nfListById = new Map(slim.map(p => [String(p.id), p]));
        }
      }

      while (hasMoreProducts) {
        const collectionResponse = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`);
        const collectionData = await collectionResponse.json();

        if (collectionData.products && collectionData.products.length > 0) {
          collectionProducts = collectionProducts.concat(collectionData.products);
          console.log(`Page ${page}: fetched ${collectionData.products.length} products (total: ${collectionProducts.length})`);
          page++;
          // Safety limit to prevent infinite loops
          if (page > 10) {
            console.warn('Reached maximum page limit (10), stopping pagination');
            hasMoreProducts = false;
          }
        } else {
          hasMoreProducts = false;
        }
      }

      console.log(`Found ${collectionProducts.length} products in collection (${page - 1} pages)`);
      if (opts.main && !collectionProducts.length) this._nfMainListFailed = true;   // NF 2026-10-23: → Hinweis

      // NF: nur Produkte einzeln laden, die ein Bereich dieser Seite überhaupt zeigen kann (gleiche Titel-Regeln
      // wie filterProductsForSection) – z. B. ~20 statt 203 Einzelabrufe
      if (typeof opts.titleFilter === 'function') {
        const before = collectionProducts.length;
        collectionProducts = collectionProducts.filter(p => opts.titleFilter(p.title));
        console.log(`[NF] Vorfilter: ${collectionProducts.length} von ${before} Produkten werden geladen`);
      }
      console.log('Fetching fresh product data with accurate prices...');

      // Step 2: Fetch each product individually to get ACCURATE, UP-TO-DATE prices
      // The Collections API returns stale/cached prices, but Product API has current prices
      // NF 2026-10-05: Schlägt ein Einzelabruf fehl (429/Netz), einmal nach 800 ms wiederholen. Klappt auch das
      // nicht und hat die Liste keine Varianten (schlanke Storefront-Liste), den Handle in _nfLoadFailed merken
      // statt still wegzulassen – loadFromURL meldet fehlende Positionen, nfAddToCart schützt den Warenkorb.
      const fetchProductJs = async (handle) => {
        for (let attempt = 0; attempt < 2; attempt++) {
          if (attempt) await new Promise(r => setTimeout(r, 800));
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (res.ok) return res;
            console.warn(`Failed to fetch ${handle} (HTTP ${res.status})${attempt ? '' : ' – neuer Versuch'}`);
          } catch (error) {
            console.warn(`Error fetching ${handle}${attempt ? '' : ' – neuer Versuch'}:`, error);
          }
        }
        return null;
      };
      const markFailed = (product) => {
        if (product.variants) return product;   // Rückfall auf Sammlungsdaten (nur mit Varianten brauchbar)
        (this._nfLoadFailed = this._nfLoadFailed || new Set()).add(product.handle);
        return null;
      };
      const productPromises = collectionProducts.map(async (product) => {
        try {
          const productResponse = await fetchProductJs(product.handle);
          if (productResponse) {
            const freshProduct = await productResponse.json();
            console.log(`✓ Loaded fresh data for: ${freshProduct.title} (${freshProduct.variants?.length || 0} variants)`);

            // Step 2b: genau 250 Varianten = vermutlich gekürzt (.js liefert höchstens 250). NF 2026-10-23: Rest über die
            // Theme-Vorlage nf-varianten (Seiten parallel, gecacht); klappt das nicht (z. B. 429), über die Storefront API
            // ohne Token; klappt auch das nicht → _nfTruncated → sichtbarer Hinweis (nfShowLoadProblemNotice), Teilen-/
            // Bearbeiten-Links nennen fehlende Positionen (loadFromURL) – nie mehr still auf 250 kürzen.
            if (freshProduct.variants && freshProduct.variants.length === 250) {
              const res = this._nfViewBroken
                ? { variants: null, status: -3 }
                : await this.fetchAllVariantsViaTheme(product.handle, freshProduct.variants);
              let all = res.variants, via = 'Theme-Vorlage nf-varianten';
              if (!all) {
                console.warn(`[NF] ${product.handle}: Vorlage nf-varianten ohne Ergebnis (${res.status}) – Storefront API ohne Token`);
                all = await this.fetchAllVariantsViaStorefront(product.handle, freshProduct);
                via = 'Storefront API ohne Token';
              }
              if (all) {
                freshProduct.variants = all;
                console.log(`✅ "${freshProduct.title}": ${all.length} Varianten (${via})`);
              } else {
                (this._nfTruncated = this._nfTruncated || new Set()).add(product.handle);
              }
            }

            return freshProduct;
          } else {
            console.warn(`Failed to fetch ${product.handle} twice, using collection data if available`);
            return markFailed(product);
          }
        } catch (error) {
          console.warn(`Error fetching ${product.handle}:`, error);
          return markFailed(product);
        }
      });

      let products = (await Promise.all(productPromises)).filter(Boolean);

      console.log(`Loaded ${products.length} products with accurate pricing`);

      // Log first product's variant data as sample
      if (products.length > 0) {
        console.log('Sample product data (first product with fresh prices):');
        console.log('  Title:', products[0].title);
        console.log('  Variants:', products[0].variants.length);
        if (products[0].variants.length > 0) {
          console.log('  First variant:', {
            id: products[0].variants[0].id,
            title: products[0].variants[0].title,
            price: products[0].variants[0].price,
            option1: products[0].variants[0].option1,
            option2: products[0].variants[0].option2,
            option3: products[0].variants[0].option3
          });
        }
      }

      // Dicke: seit 2026-10-23 keine Storefront API mehr – siehe fetchThicknessMetafields
      await this.fetchThicknessMetafields(products);

      // Sort products alphabetically by title
      products.sort((a, b) => a.title.localeCompare(b.title, 'de'));

      // Return the pool; the caller decides where it belongs.
      return products;
    } catch (error) {
      console.error(`Error fetching products for collection "${collectionHandle}":`, error);
      if (opts.main) this._nfMainListFailed = true;   // NF 2026-10-23: → Hinweis (nfShowLoadProblemNotice)
      return [];
    }
  }

  async extractAvailableColors(products) {
    console.log('=== EXTRACTING COLORS/SURFACES FROM BUNDLE PRODUCTS ===');
    console.log(`Total products to check: ${products.length}`);
    const colorsSet = new Set();

    // Option names that indicate color/surface/material (case-insensitive)
    const colorOptionNames = [
      'farbe', 'color', 'colour', 'couleur',
      'oberfläche', 'oberflaeche', 'surface',
      'material', 'holz', 'holzart', 'wood',
      'finish', 'dekor', 'decor', 'stil', 'style'
    ];

    // Log all unique option names from all products for debugging
    const allOptionNames = new Set();
    products.forEach(product => {
      if (product.options) {
        product.options.forEach(opt => {
          const name = this.getOptionName(opt);
          if (name) allOptionNames.add(name);
        });
      }
    });
    console.log('All unique option names in bundle products:', Array.from(allOptionNames));

    // Extract colors from variant options
    products.forEach((product, idx) => {
      if (!product.options || !product.variants) return;

      // Log first 3 products' options for debugging
      if (idx < 3) {
        console.log(`Product ${idx + 1}: "${product.title}"`);
        console.log(`  Options:`, product.options.map(o => this.getOptionName(o)));
        console.log(`  First variant options: ${product.variants[0]?.option1} / ${product.variants[0]?.option2} / ${product.variants[0]?.option3}`);
      }

      // Find the color/surface option index
      let colorOptionIndex = -1;
      let colorOptionName = null;

      for (let i = 0; i < product.options.length; i++) {
        const optionName = this.getOptionName(product.options[i]);
        const lowerName = optionName.toLowerCase();

        // Check if this option is a color/surface option
        if (colorOptionNames.some(name => lowerName.includes(name))) {
          colorOptionIndex = i;
          colorOptionName = optionName;
          if (idx < 3) {
            console.log(`  ✓ Found color/surface option "${colorOptionName}" at index ${i}`);
          }
          break;
        }
      }

      // If we found a color/surface option, extract all unique values
      if (colorOptionIndex !== -1) {
        product.variants.forEach(variant => {
          let colorValue = null;

          if (colorOptionIndex === 0) colorValue = variant.option1;
          else if (colorOptionIndex === 1) colorValue = variant.option2;
          else if (colorOptionIndex === 2) colorValue = variant.option3;

          if (colorValue) {
            colorsSet.add(colorValue);
          }
        });
      }
    });

    // Convert Set to sorted array
    this.availableColors = Array.from(colorsSet).sort();
    console.log(`Found ${this.availableColors.length} unique colors/surfaces:`, this.availableColors);

    if (this.availableColors.length === 0) {
      console.log('⚠️ NO COLOR OPTIONS FOUND IN BUNDLE PRODUCTS!');
      console.log('Bundle products may only have size options, not color/surface options.');
      console.log('Color sync requires bundle products to have a color/surface variant option.');
    }

    // Always render color filter
    this.renderColorFilter();
  }

  renderColorFilter() {
    if (!this.searchContainer) return;

    // Check if color filter already exists
    let colorFilter = this.searchContainer.querySelector('[data-color-filter]');

    if (!colorFilter) {
      // Create color filter dropdown
      colorFilter = document.createElement('select');
      colorFilter.className = 'bundle-color-filter';
      colorFilter.setAttribute('data-color-filter', '');

      // Add to search container (after search input)
      this.searchContainer.appendChild(colorFilter);

      // Bind event listener
      colorFilter.addEventListener('change', (e) => this.handleColorFilter(e.target.value));
    }

    // Hide the filter if no colors are available, or if the open section draws
    // from its own collection - the dropdown lists front colours, which are
    // meaningless for e.g. handles and would filter the section down to nothing.
    if (!this.availableColors || this.availableColors.length === 0
        || this.currentSectionUsesOwnCollection()) {
      colorFilter.style.display = 'none';
      colorFilter.value = '';
      return;
    }

    // Show and populate options if colors exist
    colorFilter.style.display = '';
    colorFilter.innerHTML = `
      <option value="">Alle Farben</option>
      ${this.availableColors.map(color => `
        <option value="${color}">${color}</option>
      `).join('')}
    `;
  }

  handleColorFilter(selectedColor) {
    this.selectedColorFilter = selectedColor || null;
    console.log('Color filter changed to:', this.selectedColorFilter);

    // Clear the toggled variants cache so products show the filtered color
    this.lastToggledVariantByProduct = {};

    this.renderProductsGrid(this.currentSearchTerm);
  }

  detectAndSetMainProductColor() {
    // A section with its own collection has an unrelated colour vocabulary
    // (handles: Stahl, Bronze, ...). Carrying the main product's front colour
    // over would filter that section down to nothing - also on later
    // re-detections triggered by a variant change or the URL poll.
    if (this.currentSectionUsesOwnCollection()) {
      this.selectedColorFilter = null;
      this.selectedBackgroundFilter = null;
      return;
    }
    try {
      // Try to find the selected color/surface from the main product's variant selector
      let selectedColor = null;

      // SPECIAL CASE: Theme uses name="id" radio buttons with title attribute containing color
      // This is common in swatch-based themes
      const swatchRadio = document.querySelector('input[type="radio"][name="id"]:checked');
      if (swatchRadio && swatchRadio.title) {
        selectedColor = swatchRadio.title;
        console.log(`🎨 Found color from swatch radio title: "${selectedColor}"`);
      }

      // Also try to find color from .swatch-name sibling
      if (!selectedColor) {
        const checkedSwatch = document.querySelector('.swatch-item input[type="radio"]:checked');
        if (checkedSwatch) {
          const swatchName = checkedSwatch.closest('.swatch-item')?.querySelector('.swatch-name');
          if (swatchName) {
            selectedColor = swatchName.textContent?.trim();
            console.log(`🎨 Found color from .swatch-name: "${selectedColor}"`);
          }
        }
      }

      // Option keywords to search for (color, surface, material variants)
      const optionKeywords = [
        'color', 'Color', 'colour', 'Colour',
        'farbe', 'Farbe',
        'oberfläche', 'Oberfläche', 'oberflaeche', 'Oberflaeche', 'surface', 'Surface',
        'material', 'Material',
        'holz', 'Holz', 'holzart', 'Holzart', 'wood', 'Wood',
        'finish', 'Finish',
        'dekor', 'Dekor', 'decor', 'Decor',
        'stil', 'Stil', 'style', 'Style'
      ];

      // Build selectors dynamically for all option keywords
      const colorSelectors = [];
      optionKeywords.forEach(keyword => {
        // Input radio buttons
        colorSelectors.push(`input[name*="${keyword}"]:checked`);
        // Select dropdowns
        colorSelectors.push(`select[name*="${keyword}"] option:checked`);
        // Data attribute based selectors (common in custom themes)
        colorSelectors.push(`[data-option-name*="${keyword}"] [class*="active"]`);
        colorSelectors.push(`[data-option-name*="${keyword}"] [aria-checked="true"]`);
        colorSelectors.push(`[data-option*="${keyword}"] .selected`);
        colorSelectors.push(`[data-option*="${keyword}"] .is-selected`);
      });

      // Add generic Shopify variant selectors
      colorSelectors.push(
        // Dawn theme and similar
        'variant-radios input:checked',
        'variant-selects select option:checked',
        // Swatch selectors (common in many themes)
        '.swatch input:checked',
        '.color-swatch input:checked',
        '.variant-swatch.is-active',
        '.variant-swatch.active',
        '.product-form__input input:checked',
        // Button-style variant selectors
        '.variant-button.is-selected',
        '.variant-button.active',
        '.variant-input-wrapper input:checked'
      );

      // Method 1: Try each selector to find a selected value
      for (const selector of colorSelectors) {
        try {
          const element = document.querySelector(selector);
          if (element) {
            selectedColor = element.value || element.textContent?.trim() || element.dataset.value || element.dataset.optionValue;
            if (selectedColor && selectedColor !== 'Default Title') {
              console.log(`🎨 Found selected color/surface from selector "${selector}":`, selectedColor);
              break;
            }
            selectedColor = null; // Reset if value was empty or default
          }
        } catch (e) {
          // Invalid selector, skip
        }
      }

      // Method 2: Look for visible selected state in variant picker containers
      if (!selectedColor) {
        const variantContainers = document.querySelectorAll('[data-variant-option], .variant-picker, .product-form__option');
        for (const container of variantContainers) {
          const label = container.querySelector('label, legend, .option-label, .variant-picker__label');
          if (label) {
            const labelText = label.textContent?.toLowerCase() || '';
            // Check if this container is for a color/surface option
            const isColorOption = optionKeywords.some(kw => labelText.includes(kw.toLowerCase()));
            if (isColorOption) {
              // Find the selected value in this container
              const selectedInput = container.querySelector('input:checked, select option:checked, .is-selected, .active, [aria-checked="true"]');
              if (selectedInput) {
                selectedColor = selectedInput.value || selectedInput.textContent?.trim() || selectedInput.dataset.value;
                if (selectedColor) {
                  console.log(`🎨 Found selected color/surface in variant container:`, selectedColor);
                  break;
                }
              }
            }
          }
        }
      }

      // Method 3: Check variant title in form (fallback)
      if (!selectedColor) {
        const variantInput = document.querySelector('input[name="id"], select[name="id"]');
        if (variantInput) {
          // Try data attribute first
          if (variantInput.dataset.variantTitle) {
            const variantTitle = variantInput.dataset.variantTitle;
            const parts = variantTitle.split(/[\/\-]/);
            if (parts.length > 0) {
              selectedColor = parts[0].trim();
              console.log('🎨 Extracted color/surface from variant title attribute:', selectedColor);
            }
          }
          // Try selected option text for select elements
          else if (variantInput.tagName === 'SELECT' && variantInput.selectedOptions?.[0]) {
            const optionText = variantInput.selectedOptions[0].textContent;
            const parts = optionText.split(/[\/\-]/);
            if (parts.length > 0) {
              selectedColor = parts[0].trim();
              console.log('🎨 Extracted color/surface from select option text:', selectedColor);
            }
          }
        }
      }

      // Method 4: Check URL parameters for variant info
      if (!selectedColor) {
        const urlParams = new URLSearchParams(window.location.search);
        const variantParam = urlParams.get('variant');
        if (variantParam) {
          // Try to find the variant and its options from Shopify's product JSON
          const productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json]');
          if (productJson) {
            try {
              const productData = JSON.parse(productJson.textContent);
              const variant = productData.variants?.find(v => v.id.toString() === variantParam);
              if (variant && variant.option1) {
                selectedColor = variant.option1;
                console.log('🎨 Found color/surface from product JSON and URL variant:', selectedColor);
              }
            } catch (e) {
              // JSON parse failed, skip
            }
          }
        }
      }

      // If we found a color/surface, check if it exists in our available colors
      if (selectedColor && this.availableColors.length > 0) {
        // Try to find an exact match (case-insensitive)
        const matchingColor = this.availableColors.find(
          color => color.toLowerCase() === selectedColor.toLowerCase()
        );

        if (matchingColor) {
          console.log(`✅ Auto-setting color filter to match main product: "${matchingColor}"`);
          this.selectedColorFilter = matchingColor;

          // Update the dropdown UI to show the selected color
          const colorFilterDropdown = this.searchContainer?.querySelector('[data-color-filter]');
          if (colorFilterDropdown) {
            colorFilterDropdown.value = matchingColor;
          }
        } else {
          // Try partial match (e.g., "Schwarze Eiche" contains "Eiche")
          const partialMatch = this.availableColors.find(
            color => color.toLowerCase().includes(selectedColor.toLowerCase()) ||
                     selectedColor.toLowerCase().includes(color.toLowerCase())
          );

          if (partialMatch) {
            console.log(`✅ Auto-setting color filter to partial match: "${partialMatch}" (from "${selectedColor}")`);
            this.selectedColorFilter = partialMatch;

            const colorFilterDropdown = this.searchContainer?.querySelector('[data-color-filter]');
            if (colorFilterDropdown) {
              colorFilterDropdown.value = partialMatch;
            }
          } else {
            console.log(`⚠️ Selected color "${selectedColor}" not found in bundle products. Available:`, this.availableColors);
            this.selectedColorFilter = null;
          }
        }
      } else {
        console.log('ℹ️ No color detected on main product or no colors available in bundle products');
        this.selectedColorFilter = null;
      }
    } catch (error) {
      console.error('Error detecting main product color:', error);
      this.selectedColorFilter = null;
    }
  }

  getVariantColor(variant, product) {
    // Find the color/surface option index for this product
    if (!product || !product.options) return null;

    // Option names that indicate color/surface/material (case-insensitive)
    const colorOptionNames = [
      'farbe', 'color', 'colour', 'couleur',
      'oberfläche', 'oberflaeche', 'surface',
      'material', 'holz', 'holzart', 'wood',
      'finish', 'dekor', 'decor', 'stil', 'style'
    ];

    let colorOptionIndex = -1;

    for (let i = 0; i < product.options.length; i++) {
      const optionName = this.getOptionName(product.options[i]);
      const lowerName = optionName.toLowerCase();

      // Check if this option is a color/surface option
      if (colorOptionNames.some(name => lowerName.includes(name))) {
        colorOptionIndex = i;
        break;
      }
    }

    // If we found a color/surface option, return the variant's value
    if (colorOptionIndex !== -1) {
      if (colorOptionIndex === 0) return variant.option1;
      if (colorOptionIndex === 1) return variant.option2;
      if (colorOptionIndex === 2) return variant.option3;
    }

    return null;
  }

  detectAndSetMainProductBackground() {
    // A section with its own collection has an unrelated colour vocabulary
    // (handles: Stahl, Bronze, ...). Carrying the main product's front colour
    // over would filter that section down to nothing - also on later
    // re-detections triggered by a variant change or the URL poll.
    if (this.currentSectionUsesOwnCollection()) {
      this.selectedColorFilter = null;
      this.selectedBackgroundFilter = null;
      return;
    }
    try {
      let selectedBackground = null;

      // The background option values we're looking for
      const backgroundValues = [
        'Gleiche Farbe wie die Fronten',
        'Hintergrund - Weiße Eiche',
        'Hintergrund - Natürliche Eiche',
        'Hintergrund - Pekannuss',
        'Hintergrund - Nussbaum'
      ];

      // Keywords to identify the background option
      const backgroundKeywords = [
        'hintergrund', 'Hintergrund',
        'griffe', 'Griffe',
        'hintergrund farbe', 'Hintergrund Farbe'
      ];

      // Method 1: Check all radio buttons for background option
      const allRadios = document.querySelectorAll('input[type="radio"]:checked');
      for (const radio of allRadios) {
        const value = radio.value || radio.title || '';
        // Check if this value is one of the background values
        if (backgroundValues.some(bg => value.includes(bg) || bg.includes(value))) {
          selectedBackground = value;
          break;
        }
        // Also check if the name/label contains "hintergrund" or "griffe"
        const name = (radio.name || '').toLowerCase();
        const label = radio.closest('label')?.textContent?.toLowerCase() || '';
        if (backgroundKeywords.some(kw => name.includes(kw.toLowerCase()) || label.includes(kw.toLowerCase()))) {
          selectedBackground = radio.value || radio.title;
          break;
        }
      }

      // Method 2: Check all select elements
      if (!selectedBackground) {
        const allSelects = document.querySelectorAll('select');
        for (const select of allSelects) {
          const name = (select.name || '').toLowerCase();
          const label = select.closest('.product-form__input, .variant-picker, .option-wrapper')
            ?.querySelector('label, legend')?.textContent?.toLowerCase() || '';

          if (backgroundKeywords.some(kw => name.includes(kw.toLowerCase()) || label.includes(kw.toLowerCase()))) {
            selectedBackground = select.value;
            break;
          }

          // Also check if selected value is one of the background values
          const selectedValue = select.value || '';
          if (backgroundValues.some(bg => selectedValue.includes(bg))) {
            selectedBackground = selectedValue;
            break;
          }
        }
      }

      // Method 3: Check variant containers with background in label
      if (!selectedBackground) {
        const variantContainers = document.querySelectorAll('[data-variant-option], .variant-picker, .product-form__option, .product-form__input');
        for (const container of variantContainers) {
          const label = container.querySelector('label, legend, .option-label')?.textContent?.toLowerCase() || '';
          if (backgroundKeywords.some(kw => label.includes(kw.toLowerCase()))) {
            const selectedInput = container.querySelector('input:checked, select option:checked, .is-selected, .active');
            if (selectedInput) {
              selectedBackground = selectedInput.value || selectedInput.textContent?.trim();
              break;
            }
          }
        }
      }

      // Set the filter if we found a background selection
      this.selectedBackgroundFilter = selectedBackground || null;
    } catch (error) {
      console.error('Error detecting main product background:', error);
      this.selectedBackgroundFilter = null;
    }
  }

  handleSearch(searchTerm) {
    this.currentSearchTerm = searchTerm;
    this.renderProductsGrid(searchTerm);
  }

  renderProductsGrid(searchTerm = '') {
    // Add stack trace to see what's calling this re-render
    console.log('🔄 renderProductsGrid() called');
    console.trace('Call stack:');

    if (this.availableProducts.length === 0) {
      this.productsGrid.innerHTML = '<div class="bundle-empty-state">Keine Produkte verfügbar</div>';
      return;
    }

    // Filter products based on search term
    let filteredProducts = this.availableProducts;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filteredProducts = this.availableProducts.filter(product =>
        product.title.toLowerCase().includes(term) ||
        product.vendor?.toLowerCase().includes(term) ||
        product.product_type?.toLowerCase().includes(term)
      );
    }

    // Filter products based on selected color
    if (this.selectedColorFilter) {
      console.log('Filtering by color:', this.selectedColorFilter);
      filteredProducts = filteredProducts.filter(product => {
        // Check if any variant has the selected color
        return product.variants.some(variant => {
          const variantColor = this.getVariantColor(variant, product);
          return variantColor === this.selectedColorFilter;
        });
      });
      console.log(`Filtered to ${filteredProducts.length} products with color "${this.selectedColorFilter}"`);
    }

    if (filteredProducts.length === 0) {
      this.productsGrid.innerHTML = '<div class="bundle-empty-state">Keine Produkte gefunden</div>';
      return;
    }

    this.productsGrid.innerHTML = filteredProducts.map(product => {
      const image = this.getProductImage(product);

      // Check if any variant of this product is selected FOR THE CURRENT SECTION
      // We need to determine which variant is currently shown in the card
      const currentVariant = this.getCurrentCardVariant(product);

      // Check if actually selected for button state
      const isActuallySelected = currentVariant ?
        this.isVariantSelectedInSection(currentVariant.id.toString(), this.currentSectionId) : false;

      const selectedData = isActuallySelected ?
        this.selectedProducts.get(`${this.currentSectionId}_${currentVariant.id}`) : null;

      // NEVER show visual indicators in the grid - "Already in Bundle" section shows selections
      // Products in the grid always look fresh and available
      const showAsSelected = false;

      return this.renderProductCard(product, image, showAsSelected, selectedData, currentVariant, isActuallySelected);
    }).join('');

    this.bindProductCardEvents();
  }

  isVariantSelectedInSection(variantId, sectionId) {
    const compoundKey = `${sectionId}_${variantId}`;
    return this.selectedProducts.has(compoundKey);
  }

  getCurrentCardVariant(product) {
    // If we just toggled a variant for this product, use that one
    if (this.lastToggledVariantByProduct && this.lastToggledVariantByProduct[product.id]) {
      return this.lastToggledVariantByProduct[product.id];
    }

    // Otherwise, try to find a selected variant for this product IN THE CURRENT SECTION
    for (let variant of product.variants) {
      const compoundKey = `${this.currentSectionId}_${variant.id}`;
      if (this.selectedProducts.has(compoundKey)) {
        const productData = this.selectedProducts.get(compoundKey);
        return productData.selectedVariant;
      }
    }

    // If a color filter is active, try to find a variant matching that color
    if (this.selectedColorFilter) {
      const matchingVariant = product.variants.find(variant => {
        const variantColor = this.getVariantColor(variant, product);
        return variantColor === this.selectedColorFilter;
      });

      if (matchingVariant) {
        console.log(`Auto-selecting variant with color "${this.selectedColorFilter}" for product: ${product.title}`);
        return matchingVariant;
      }
    }

    // Fall back to first variant
    return product.variants[0];
  }

  getProductImage(product) {
    // Handle different image URL formats from Shopify API
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      // Image can be a string URL or an object with src property
      if (typeof img === 'string') {
        return img;
      } else if (img.src) {
        return img.src;
      }
    }
    if (product.image && product.image.src) {
      return product.image.src;
    }
    if (product.featured_image) {
      return product.featured_image;
    }
    // Return placeholder if no image
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23eee" width="200" height="200"/%3E%3C/svg%3E';
  }

  getProductDisplayTitle(product) {
    // First check if we have a cached subtitle for this product
    this._productSubtitleCache = this._productSubtitleCache || {};
    const productIdStr = String(product.id);

    // Check cache first
    if (this._productSubtitleCache[productIdStr]) {
      return this._productSubtitleCache[productIdStr];
    }

    // Check Liquid-loaded subtitles (from metafields)
    const subtitles = this.settings.productSubtitles || {};
    const subtitle = subtitles[productIdStr];

    if (subtitle && subtitle.trim()) {
      this._productSubtitleCache[productIdStr] = subtitle;
      return subtitle;
    }

    // Check if product has metafields loaded from JS fetch
    if (product.metafields && product.metafields.descriptors && product.metafields.descriptors.subtitle) {
      const jsSubtitle = product.metafields.descriptors.subtitle;
      if (jsSubtitle && jsSubtitle.trim()) {
        this._productSubtitleCache[productIdStr] = jsSubtitle;
        return jsSubtitle;
      }
    }

    // Display mode: front titles end in the meaningful part ("... - Tür"), so
    // the last segment is right there. Handle titles do not
    // ("Fronten Griffe - Dalo - 286 x 24 mm") - keeping only the last segment
    // drops the model name and leaves the customer with a bare size.
    // A product from a section collection follows a different title scheme:
    // fronts end in the element ("... - Tuer"), handles end in the size
    // ("Fronten Griffe - Dalo - 286 x 24 mm") - keeping only the last segment
    // would drop the model name. Decide per product, not per block.
    const fromSectionCollection = (this.sectionCollectionProductIds || new Set()).has(productIdStr);
    const mode = fromSectionCollection ? 'strip_first' : (this.settings.titleDisplay || 'last');
    if (mode === 'full') {
      this._productSubtitleCache[productIdStr] = product.title;
      return product.title;
    }
    if (mode === 'strip_first') {
      const parts = String(product.title).split(' - ');
      const stripped = parts.length >= 2 ? parts.slice(1).join(' - ').trim() : product.title;
      this._productSubtitleCache[productIdStr] = stripped;
      return stripped;
    }

    // Fallback: Parse the product title to extract the last meaningful part
    const parsedSubtitle = this.parseProductTitleToSubtitle(product.title);
    if (parsedSubtitle) {
      this._productSubtitleCache[productIdStr] = parsedSubtitle;
      return parsedSubtitle;
    }

    // Last fallback to full title
    return product.title;
  }

  parseProductTitleToSubtitle(title) {
    if (!title) return null;

    // Split by " - " separator
    const parts = title.split(' - ');

    // If we have multiple parts, take the last one as subtitle
    if (parts.length >= 2) {
      // Get the last part (the product type)
      return parts[parts.length - 1].trim();
    }

    // If no separator found, return null to use full title
    return null;
  }

  renderProductCard(product, image, isSelected, selectedData, currentVariant, isActuallySelected) {
    const hasMultipleVariants = product.variants.length > 1;
    const selectedVariant = currentVariant || selectedData?.selectedVariant || product.variants[0];
    const quantity = selectedData?.quantity || 1;
    const price = selectedVariant?.price || product.variants[0]?.price;
    const compareAtPrice = selectedVariant?.compare_at_price || product.variants[0]?.compare_at_price;

    // Build price display HTML
    let priceHtml = '';
    if (compareAtPrice && compareAtPrice > price) {
      // On sale - show compare-at price crossed out and sale price
      priceHtml = `
        <span class="bundle-price-compare">${this.formatMoney(compareAtPrice)}</span>
        <span class="bundle-price-sale">${this.formatMoney(price)}</span>
      `;
    } else {
      // Regular price
      priceHtml = `<span class="bundle-price-current">${this.formatMoney(price)}</span>`;
    }

    // Check if single variant has a meaningful title (not "Default Title")
    const singleVariantTitle = !hasMultipleVariants && selectedVariant.title && selectedVariant.title !== 'Default Title'
      ? selectedVariant.title
      : null;

    const variantOptions = hasMultipleVariants ? this.renderVariantOptions(product, selectedVariant) : '';

    const borderColor = this.settings.selectionBorderColor || '#000000';

    return `
      <div
        class="bundle-product-card ${isSelected ? 'selected' : ''}"
        data-product-id="${product.id}"
        data-variant-id="${selectedVariant.id}"
        style="${isSelected ? `border-color: ${borderColor};` : ''}"
      >
        ${isSelected ? '<div class="bundle-added-badge">Im Bundle</div>' : ''}
        <img src="${image}" alt="${this.getProductDisplayTitle(product)}" class="bundle-product-image" loading="lazy" />
        <div class="bundle-product-title">${this.getProductDisplayTitle(product)}</div>
        <div class="bundle-product-price" data-price-display="${selectedVariant.id}" data-variant-price="${price}" data-variant-compare-price="${compareAtPrice || ''}">${priceHtml}</div>

        <div class="bundle-product-options">
          ${variantOptions}
          ${singleVariantTitle ? `
            <div class="bundle-product-option bundle-single-variant${product._thickness ? ' bundle-option-with-dicke' : ''}">
              <div class="${product._thickness ? 'bundle-option-col' : ''}">
                <label>Größe</label>
                <span class="bundle-single-variant-value">${singleVariantTitle}</span>
              </div>
              ${product._thickness ? `
                <div class="bundle-option-col bundle-dicke-col">
                  <label>Dicke</label>
                  <span class="bundle-dicke-value">${product._thickness} mm</span>
                </div>
              ` : ''}
            </div>
          ` : ''}
          <div class="bundle-product-option">
            <label>Anzahl</label>
            <div class="bundle-quantity-selector">
              <div class="bundle-quantity-btn bundle-quantity-minus" data-quantity-decrease="${product.id}" data-product-id="${product.id}" onclick="event.stopPropagation()">-</div>
              <div class="bundle-quantity-label" data-quantity-display="${product.id}">${quantity}</div>
              <input
                type="hidden"
                min="1"
                value="${quantity}"
                data-quantity-input="${product.id}"
                data-quantity-variant-id="${selectedVariant.id}"
              />
              <div class="bundle-quantity-btn bundle-quantity-plus" data-quantity-increase="${product.id}" data-product-id="${product.id}" onclick="event.stopPropagation()">+</div>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="bundle-add-product-btn overlay-bundle-btn${this.isVariantAvailable(selectedVariant) ? '' : ' is-unavailable'}"
          data-add-product="${product.id}"
          ${this.isVariantAvailable(selectedVariant) ? '' : 'disabled'}
          onclick="event.stopPropagation()"
        >
          ${this.isVariantAvailable(selectedVariant) ? 'Hinzufügen' : 'Nicht verfügbar'}
        </button>
      </div>
    `;
  }

  // NF 2026-10-23: kein Abruf mehr (vorher Storefront API mit Token im Seitenquelltext, seit 22.10. 401). Die Dicke
  // zeigte nur die alte Produktkarte (renderProductCard/renderVariantOptions); das Konfigurator-Fenster aus
  // nf-bundle-ux.js zeigt keine Dicke. Ohne _thickness bleibt die Dicke-Spalte der alten Karte einfach weg.
  async fetchThicknessMetafields(products) {
    const withThickness = products.filter(p => p._thickness).length;
    console.log(`[Thickness] Loaded thickness for ${withThickness}/${products.length} products`);
  }

  renderVariantOptions(product, selectedVariant) {
    const options = this.getProductOptions(product);

    console.log('Rendering variant options, got options:', options);

    return options.map(optionObj => {
      console.log('Processing option object:', optionObj);
      const optionName = String(optionObj.name || 'Option');
      let values = optionObj.values;

      console.log(`  Option: "${optionName}", Available values:`, values);

      // Find option index by comparing names
      const optionIndex = product.options.findIndex(opt =>
        this.getOptionName(opt) === optionName
      );

      // Get selected variant value for this option
      let preSelectedValue;
      if (optionIndex === 0) preSelectedValue = selectedVariant.option1;
      else if (optionIndex === 1) preSelectedValue = selectedVariant.option2;
      else if (optionIndex === 2) preSelectedValue = selectedVariant.option3;

      const isGroesse = optionName === 'Größe';
      const thickness = isGroesse ? product._thickness : null;

      return `
        <div class="bundle-product-option${thickness ? ' bundle-option-with-dicke' : ''}">
          <div class="${thickness ? 'bundle-option-col' : ''}">
            <label>${optionName}</label>
            <select
              data-variant-select="${product.id}"
              data-option-name="${optionName}"
              onclick="event.stopPropagation()"
            >
              ${values.map(value => `
                <option value="${value}" ${value === preSelectedValue ? 'selected' : ''}>
                  ${value}
                </option>
              `).join('')}
            </select>
          </div>
          ${thickness ? `
            <div class="bundle-option-col bundle-dicke-col">
              <label>Dicke</label>
              <span class="bundle-dicke-value">${thickness} mm</span>
            </div>
          ` : ''}
        </div>
      `;
    }).join('');
  }

  getProductOptions(product) {
    const optionsMap = {};

    console.log('Getting product options for:', product.title);
    console.log('Product options array:', product.options);
    console.log('Product has variants:', product.variants.length);
    console.log('All variant option values:');
    product.variants.forEach((v, i) => {
      console.log(`  Variant ${i}: option1="${v.option1}", option2="${v.option2}", option3="${v.option3}"`);
    });

    product.variants.forEach(variant => {
      if (variant.option1 && product.options && product.options[0]) {
        const optionName = this.getOptionName(product.options[0]);
        if (!optionsMap[optionName]) {
          optionsMap[optionName] = new Set();
        }
        optionsMap[optionName].add(variant.option1);
      }
      if (variant.option2 && product.options && product.options[1]) {
        const optionName = this.getOptionName(product.options[1]);
        if (!optionsMap[optionName]) {
          optionsMap[optionName] = new Set();
        }
        optionsMap[optionName].add(variant.option2);
      }
      if (variant.option3 && product.options && product.options[2]) {
        const optionName = this.getOptionName(product.options[2]);
        if (!optionsMap[optionName]) {
          optionsMap[optionName] = new Set();
        }
        optionsMap[optionName].add(variant.option3);
      }
    });

    const result = Object.keys(optionsMap).map(name => ({
      name: String(name),
      values: Array.from(optionsMap[name])
    }));

    console.log('Generated options:', result);
    return result;
  }

  bindProductCardEvents() {
    console.log('🔗 BINDING PRODUCT CARD EVENTS');
    const cards = this.productsGrid.querySelectorAll('.bundle-product-card');
    console.log(`Found ${cards.length} product cards to bind`);

    this.productsGrid.querySelectorAll('.bundle-product-card').forEach((card, cardIndex) => {
      const productId = parseInt(card.dataset.productId);
      const product = this.availableProducts.find(p => p.id === productId);
      if (!product) {
        console.error('Product not found for card:', productId);
        return;
      }

      console.log(`Card ${cardIndex}: ${product.title}`);

      // Handle Add/Remove button clicks - ONLY way to add products to bundle
      const addButton = card.querySelector('[data-add-product]');
      if (addButton) {
        addButton.addEventListener('click', (e) => {
          e.stopPropagation();
          const currentVariant = this.getSelectedVariantFromCard(card, product);
          this.toggleProductSelection(product, currentVariant);
        });
        console.log(`  ✓ Bound click event to Add button`);
      }

      // REMOVED: Card click handler - customers must click the button to add products

      // Handle variant changes with improved logging
      const variantSelects = card.querySelectorAll('[data-variant-select]');
      console.log(`  Found ${variantSelects.length} variant selects`);

      variantSelects.forEach((select, selectIndex) => {
        console.log(`    Select ${selectIndex}: ${select.dataset.optionName}`);

        select.addEventListener('change', (e) => {
          e.stopPropagation();
          console.log('=== VARIANT OPTION CHANGED ===');
          console.log('Product:', product.title);
          console.log('Option changed:', select.dataset.optionName, '→', select.value);

          // Mark this product as user-modified so color sync won't override it
          this.userModifiedVariants.add(product.id);
          this.updateProductVariant(card, product);
        });
        console.log(`    ✓ Bound change event listener to select`);
      });

      // Handle quantity changes
      const quantityInput = card.querySelector('[data-quantity-input]');
      const quantityDisplay = card.querySelector('[data-quantity-display]');

      if (quantityInput) {
        // Handle quantity decrease button
        const decreaseBtn = card.querySelector('[data-quantity-decrease]');
        if (decreaseBtn) {
          decreaseBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const currentValue = parseInt(quantityInput.value) || 1;
            if (currentValue > 1) {
              const newValue = currentValue - 1;
              quantityInput.value = newValue;
              if (quantityDisplay) quantityDisplay.textContent = newValue;
              const variantId = parseInt(quantityInput.dataset.quantityVariantId);
              this.updateProductQuantity(variantId, newValue);
            }
          });
        }

        // Handle quantity increase button
        const increaseBtn = card.querySelector('[data-quantity-increase]');
        if (increaseBtn) {
          increaseBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const currentValue = parseInt(quantityInput.value) || 1;
            const newValue = currentValue + 1;
            quantityInput.value = newValue;
            if (quantityDisplay) quantityDisplay.textContent = newValue;
            const variantId = parseInt(quantityInput.dataset.quantityVariantId);
            this.updateProductQuantity(variantId, newValue);
          });
        }
      }
    });
  }

  getSelectedVariantFromCard(card, product) {
    const selects = card.querySelectorAll('[data-variant-select]');
    const selectedOptions = {};

    console.log('━━━ GETTING SELECTED VARIANT ━━━');
    console.log('Product:', product.title);
    console.log('Product has', product.variants.length, 'variants total');
    console.log('Found', selects.length, 'variant selects in card');

    // Log all variants and their prices for comparison
    console.log('All available variants for this product:');
    product.variants.forEach((v, idx) => {
      console.log(`  [${idx}] ID: ${v.id}, Options: ${v.option1}/${v.option2}/${v.option3}, Price: ${v.price}`);
    });

    selects.forEach(select => {
      selectedOptions[select.dataset.optionName] = select.value;
      console.log(`  Selected: ${select.dataset.optionName} = "${select.value}"`);
    });

    // Find matching variant based on selected options
    const variant = product.variants.find(v => {
      const matches = [];
      if (product.options[0]) {
        const optionName = this.getOptionName(product.options[0]);
        const match = v.option1 === selectedOptions[optionName];
        matches.push(match);
        if (!match) {
          console.log(`    Variant ${v.id}: option1 "${v.option1}" !== "${selectedOptions[optionName]}" ✗`);
        }
      }
      if (product.options[1]) {
        const optionName = this.getOptionName(product.options[1]);
        const match = v.option2 === selectedOptions[optionName];
        matches.push(match);
        if (!match) {
          console.log(`    Variant ${v.id}: option2 "${v.option2}" !== "${selectedOptions[optionName]}" ✗`);
        }
      }
      if (product.options[2]) {
        const optionName = this.getOptionName(product.options[2]);
        const match = v.option3 === selectedOptions[optionName];
        matches.push(match);
        if (!match) {
          console.log(`    Variant ${v.id}: option3 "${v.option3}" !== "${selectedOptions[optionName]}" ✗`);
        }
      }
      return matches.length > 0 && matches.every(m => m);
    });

    if (variant) {
      console.log('✓✓✓ MATCHED VARIANT ✓✓✓');
      console.log('  ID:', variant.id);
      console.log('  Title:', variant.title);
      console.log('  Options:', variant.option1, variant.option2, variant.option3);
      console.log('  Price:', variant.price);
      console.log('  Full variant object:', variant);
    } else {
      console.error('✗✗✗ NO MATCHING VARIANT FOUND ✗✗✗');
      console.log('Using first variant as fallback:', product.variants[0]);
    }

    return variant || product.variants[0];
  }

  /**
   * A variant counts as available unless Shopify explicitly says otherwise.
   * Defaulting to true keeps products usable when the field is missing, which
   * happens for data that did not come through the product JSON.
   */
  /**
   * Shopify prefixes an app block's id with a token that changes between
   * themes and templates ("Abc123__bundle_app_nf_bundle_selector_9myKqF").
   * Only the part after "__" identifies the block itself. Comparing the full
   * id made every shared link fail as soon as it was opened from a different
   * theme or after the product moved to another template.
   */
  stableBlockId(id) {
    return String(id == null ? '' : id).split('__').pop();
  }

  /** Storage key that survives a theme or template change. */
  storageKey() {
    return `bundle_selections_${this.stableBlockId(this.settings.blockId)}_${this.settings.productId}`;
  }

  isVariantAvailable(variant) {
    return !variant || variant.available !== false;
  }

  toggleProductSelection(product, variant) {
    // Never let a sold-out variant into the bundle. Without this the batch add
    // fails at checkout time and the one-by-one fallback could leave a partial
    // bundle in the cart.
    if (!this.isVariantAvailable(variant)) {
      console.warn('Refusing sold-out variant:', product && product.title, variant && variant.title);
      this.showToast
        ? this.showToast('Diese Variante ist derzeit nicht verfügbar.')
        : alert('Diese Variante ist derzeit nicht verfügbar.');
      return;
    }

    // User is interacting - no longer fresh open state
    this.isModalFreshOpen = false;

    console.log('➕ toggleProductSelection() called');
    console.log('  Product:', product.title);
    console.log('  Variant ID:', variant.id, '(type:', typeof variant.id, ')');
    console.log('  Variant title:', variant.title);
    console.log('  Section ID:', this.currentSectionId);

    // Use compound key: sectionId_variantId to allow same variant in different sections
    const variantKey = `${this.currentSectionId}_${variant.id}`;
    console.log('  Compound key:', `"${variantKey}"`);

    let isNewProduct = false;
    let newQuantity = 1;

    if (this.selectedProducts.has(variantKey)) {
      // Product already selected - increment quantity instead of removing
      console.log('  → Product already in bundle, incrementing quantity');
      const productData = this.selectedProducts.get(variantKey);
      productData.quantity = (productData.quantity || 1) + 1;
      newQuantity = productData.quantity;
      this.selectedProducts.set(variantKey, productData);
      console.log('  → New quantity:', productData.quantity);
    } else {
      // Select this variant and track which section it belongs to
      console.log('  → Adding to selection');
      isNewProduct = true;
      this.selectedProducts.set(variantKey, {
        product: product,
        selectedVariant: variant,
        quantity: 1,
        sectionId: this.currentSectionId
      });
    }

    console.log('  Total selected products after toggle:', this.selectedProducts.size);

    // Show success toast notification
    this.showProductAddedToast(product, variant, isNewProduct, newQuantity);

    // Remember which variant was just toggled for this product
    this.lastToggledVariantByProduct = this.lastToggledVariantByProduct || {};
    this.lastToggledVariantByProduct[product.id] = variant;

    // Update the already selected section
    this.renderAlreadySelectedItems();

    // Update the selection summary in footer
    this.updateSelectionSummary();

    this.renderProductsGrid();
  }

  updateProductVariant(card, product) {
    console.log('=== UPDATE PRODUCT VARIANT ===');
    console.log('Product:', product.title);
    console.log('Card element:', card);
    console.log('Card is in DOM:', document.contains(card));

    // User is interacting - no longer fresh open state
    this.isModalFreshOpen = false;

    const newVariant = this.getSelectedVariantFromCard(card, product);

    if (!newVariant) {
      console.error('❌ Could not find variant for product:', product.title);
      return;
    }

    // Remember which variant was selected for this product
    this.lastToggledVariantByProduct = this.lastToggledVariantByProduct || {};
    this.lastToggledVariantByProduct[product.id] = newVariant;

    console.log('New variant ID:', newVariant.id);
    console.log('New variant title:', newVariant.title);
    console.log('New variant price (raw):', newVariant.price);
    console.log('New variant price type:', typeof newVariant.price);

    // Update card's variant ID without affecting already selected variants
    card.dataset.variantId = newVariant.id;
    console.log('✓ Updated card variant ID to:', card.dataset.variantId);

    // Update quantity input data attribute
    const quantityInput = card.querySelector('[data-quantity-input]');
    if (quantityInput) {
      quantityInput.dataset.quantityVariantId = newVariant.id;
      console.log('✓ Updated quantity input variant ID');
    }

    // Update the price display
    console.log('🔍 Searching for price element in card...');
    const priceElement = card.querySelector('.bundle-product-price');
    console.log('Price element found:', !!priceElement);
    console.log('Price element:', priceElement);

    if (priceElement) {
      console.log('📊 BEFORE UPDATE:');
      console.log('  Current price text:', priceElement.textContent);
      console.log('  Current data-price-display:', priceElement.getAttribute('data-price-display'));

      if (newVariant.price !== undefined && newVariant.price !== null) {
        const price = newVariant.price;
        const compareAtPrice = newVariant.compare_at_price;

        // Build price HTML with compare-at price support
        let priceHtml = '';
        if (compareAtPrice && compareAtPrice > price) {
          // On sale - show compare-at price crossed out and sale price
          priceHtml = `
            <span class="bundle-price-compare">${this.formatMoney(compareAtPrice)}</span>
            <span class="bundle-price-sale">${this.formatMoney(price)}</span>
          `;
        } else {
          // Regular price
          priceHtml = `<span class="bundle-price-current">${this.formatMoney(price)}</span>`;
        }

        console.log('💰 Formatted price HTML:', priceHtml);

        // Update the price with explicit logging
        const oldPrice = priceElement.textContent;
        const oldPriceValue = oldPrice.trim();

        // Update with new HTML structure
        priceElement.innerHTML = priceHtml;
        priceElement.setAttribute('data-price-display', newVariant.id);
        priceElement.setAttribute('data-variant-price', price);
        priceElement.setAttribute('data-variant-compare-price', compareAtPrice || '');

        // Remove any inline styles that might interfere
        priceElement.removeAttribute('style');

        console.log('📊 AFTER UPDATE:');
        console.log('  Old price was:', oldPriceValue);
        console.log('  New price is:', priceElement.textContent);
        console.log('  New variant ID:', newVariant.id);
        console.log('  Element still in DOM:', document.contains(priceElement));
        console.log('✅✅✅ PRICE UPDATED SUCCESSFULLY ✅✅✅');
      } else {
        console.error('❌ Variant has no price property!');
        console.error('Variant object:', newVariant);
      }
    } else {
      console.error('❌❌❌ Could not find price element in card!');
      console.log('Card HTML:', card.innerHTML.substring(0, 500));
      console.log('Trying alternative selectors...');

      const allPriceElements = card.querySelectorAll('[class*="price"]');
      console.log('Found elements with "price" in class:', allPriceElements.length);
      allPriceElements.forEach((el, i) => {
        console.log(`  Price element ${i}:`, el.className, el.textContent);
      });
    }

    // Keep the button in sync with the newly selected variant's availability
    const addButton = card.querySelector('[data-add-product]');

    if (addButton) {
      addButton.classList.remove('added');
      const available = this.isVariantAvailable(newVariant);
      addButton.disabled = !available;
      addButton.classList.toggle('is-unavailable', !available);
      addButton.textContent = available ? 'Hinzufügen' : 'Nicht verfügbar';
      console.log('✓ Button state:', addButton.textContent, '(available:', available + ')');
    }

    // NEVER show visual selection state in grid - only in "Already in Bundle" section
    card.classList.remove('selected');
    card.style.borderColor = '';

    console.log('=== END UPDATE PRODUCT VARIANT ===');
  }

  updateProductQuantity(variantId, quantity) {
    // Use compound key for the current section
    const variantKey = `${this.currentSectionId}_${variantId}`;

    if (!this.selectedProducts.has(variantKey)) {
      // Auto-select when quantity is changed
      const card = this.productsGrid.querySelector(`[data-variant-id="${variantId}"]`);
      if (card) {
        const productId = parseInt(card.dataset.productId);
        const product = this.availableProducts.find(p => p.id === productId);
        const variant = product.variants.find(v => v.id === variantId);

        if (variant) {
          this.selectedProducts.set(variantKey, {
            product: product,
            selectedVariant: variant,
            quantity: Math.max(1, quantity),
            sectionId: this.currentSectionId
          });
          this.renderProductsGrid();
        }
      }
    } else {
      const productData = this.selectedProducts.get(variantKey);
      productData.quantity = Math.max(1, quantity);
      this.selectedProducts.set(variantKey, productData);
    }
  }

  confirmSelection() {
    console.log('📝 confirmSelection() called');
    console.log('  Current section ID:', this.currentSectionId);
    console.log('  Modal display style before close:', this.modal.style.display);

    // Mark that modal has been shown for this section in this session
    if (this.currentSectionId) {
      const modalShownKey = `bundle_modal_shown_${this.currentSectionId}`;
      sessionStorage.setItem(modalShownKey, 'true');
      console.log('  ✓ Session storage set:', modalShownKey);
    }

    // Render products for the current section
    this.renderSelectedProductsForSection(this.currentSectionId);

    // Save selections to localStorage
    this.saveToLocalStorage();

    // Update price and button state BEFORE closing the modal
    // This ensures has-products class and inline styles are set while the
    // body.bundle-modal-open nuclear z-index rule is still active, preventing
    // a race condition on slower mobile devices where the style recomputation
    // from removing bundle-modal-open would interfere with the price display.
    this.updateBundleAddToCartButton();
    this.updateSectionHeaderSummaries();

    console.log('  Calling closeModal()...');
    this.closeModal();

    console.log('  Modal display style after close:', this.modal.style.display);

    // On mobile, do a secondary reflow after the browser has finished
    // recomputing styles from the modal close (removing bundle-modal-open).
    // Use requestAnimationFrame + setTimeout to ensure we run after the
    // browser has painted the post-modal-close state.
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.updateBundleAddToCartButton();

        // Force a DOM reflow on mobile to ensure CSS changes are applied
        if (window.innerWidth <= 767) {
          if (this.selectedProducts.size > 0) {
            this.element.classList.remove('has-products');
            void this.element.offsetHeight;
            this.element.classList.add('has-products');
          }
        }

        console.log('✅ Mobile price update triggered after modal close');
      }, 50);
    });
  }

  renderSelectedProductsForSection(sectionId) {
    // Get the container for this section
    const sectionContainer = this.scopedQuery(`[data-selected-products][data-section-id="${sectionId}"]`);
    if (!sectionContainer) {
      console.error('Could not find container for section:', sectionId);
      return;
    }

    // Filter selected products for this section
    const sectionProducts = Array.from(this.selectedProducts.values()).filter(
      item => item.sectionId === sectionId
    );

    // Select button HTML (always shown at the end)
    const selectButtonHtml = `
      <button
        type="button"
        class="bundle-select-button"
        data-open-modal
        data-section-id="${sectionId}"
      >
        <span class="bundle-select-button-icon">+</span>
        <span>Produkte auswählen</span>
      </button>
    `;

    if (sectionProducts.length === 0) {
      sectionContainer.innerHTML = selectButtonHtml;
      // Bind event to the select button
      this.bindSelectButtonEvents(sectionContainer);
      this.updateBundleAddToCartButton();
      return;
    }

    const productsHtml = sectionProducts.map(item => {
      const { product, selectedVariant, quantity } = item;
      const variantTitle = this.getVariantTitle(selectedVariant, product);
      const price = this.formatMoney(selectedVariant.price);
      const image = this.getProductImage(product);
      const colorName = this.getVariantColor(selectedVariant, product);
      const swatchHtml = colorName ? this.getSwatchHtml(colorName) : '';
      const displayTitle = this.getProductDisplayTitle(product);

      return `
        <div class="bundle-selected-product" data-selected-variant-id="${selectedVariant.id}">
          <button
            type="button"
            class="bundle-selected-product-remove"
            data-remove-variant="${selectedVariant.id}"
            data-remove-section="${sectionId}"
            aria-label="Remove ${displayTitle} - ${variantTitle}"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
          <img src="${image}" alt="${displayTitle}" class="bundle-selected-product-image" />
          <div class="bundle-selected-product-details">
            <div class="bundle-selected-product-title">${displayTitle}</div>
            ${variantTitle ? `<div style="font-size: 12px; font-weight: 500; color: #666; margin-top: 0.25rem; display: flex; align-items: center; gap: 0.375rem;">${swatchHtml}${variantTitle}</div>` : ''}
            <div style="font-size: 12px; font-weight: 500; margin-top: 0.25rem;">${price}</div>
            <div style="font-size: 12px; font-weight: 500; color: #666; margin-top: 0.25rem;">Anzahl: ${quantity}</div>
          </div>
        </div>
      `;
    }).join('');

    // Combine products HTML with select button
    sectionContainer.innerHTML = productsHtml + selectButtonHtml;

    // Bind remove button events
    sectionContainer.querySelectorAll('[data-remove-variant]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.removeVariant;
        const sectionId = btn.dataset.removeSection;
        this.removeSelectedProduct(variantId, sectionId);
      });
    });

    // Bind event to the select button
    this.bindSelectButtonEvents(sectionContainer);

    // Update the bundle add to cart button
    this.updateBundleAddToCartButton();
  }

  bindSelectButtonEvents(container) {
    const selectBtn = container.querySelector('[data-open-modal]');
    if (selectBtn) {
      selectBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const sectionId = selectBtn.dataset.sectionId;
        this.openModal(sectionId);
      });
    }
  }

  renderSelectedProductsDisplay() {
    if (this.selectedProducts.size === 0) {
      this.selectedProductsContainer.innerHTML = '';
      this.updateBundleAddToCartButton();
      return;
    }

    const selectedArray = Array.from(this.selectedProducts.values());

    this.selectedProductsContainer.innerHTML = selectedArray.map(item => {
      const { product, selectedVariant, quantity } = item;
      const variantTitle = this.getVariantTitle(selectedVariant, product);
      const price = this.formatMoney(selectedVariant.price);
      const image = this.getProductImage(product);
      const displayTitle = this.getProductDisplayTitle(product);

      return `
        <div class="bundle-selected-product" data-selected-variant-id="${selectedVariant.id}">
          <button
            type="button"
            class="bundle-selected-product-remove"
            data-remove-variant="${selectedVariant.id}"
            aria-label="Remove ${displayTitle} - ${variantTitle}"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
          <img src="${image}" alt="${displayTitle}" class="bundle-selected-product-image" />
          <div class="bundle-selected-product-details">
            <div class="bundle-selected-product-title">${displayTitle}</div>
            ${variantTitle ? `<div style="font-size: 12px; font-weight: 500; color: #666; margin-top: 0.25rem;">${variantTitle}</div>` : ''}
            <div style="font-size: 12px; font-weight: 500; margin-top: 0.25rem;">${price}</div>
            <div style="font-size: 12px; font-weight: 500; color: #666; margin-top: 0.25rem;">Anzahl: ${quantity}</div>
          </div>
        </div>
      `;
    }).join('');

    // Bind remove button events
    this.selectedProductsContainer.querySelectorAll('[data-remove-variant]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.removeVariant;
        this.removeSelectedProduct(variantId);
      });
    });

    // Update the bundle add to cart button
    this.updateBundleAddToCartButton();
  }

  removeSelectedProduct(variantId, sectionId) {
    console.log('🗑️ removeSelectedProduct() called');
    console.log('  variantId:', variantId);
    console.log('  sectionId:', sectionId);

    // Use compound key to remove from specific section
    const compoundKey = `${sectionId}_${variantId}`;
    console.log('  Compound key:', compoundKey);
    console.log('  Key exists in selectedProducts?', this.selectedProducts.has(compoundKey));

    const deleted = this.selectedProducts.delete(compoundKey);
    console.log('  Delete successful?', deleted);
    console.log('  Remaining selected products:', this.selectedProducts.size);

    // Re-render the specific section on main page
    console.log('  Re-rendering section:', sectionId);
    this.renderSelectedProductsForSection(sectionId);

    // Always update section header summaries (regardless of modal state)
    this.updateSectionHeaderSummaries();

    // Update the modal if it's open
    if (this.modal && this.modal.style.display === 'flex') {
      console.log('  Modal is open - updating "Already in Bundle" section and products grid');
      this.renderAlreadySelectedItems(); // Update the "Already in Bundle" section
      this.updateSelectionSummary(); // Update the selection summary in footer
      this.renderProductsGrid(); // Update the products grid
    }

    // Save to localStorage
    this.saveToLocalStorage();
  }

  updateBundleAddToCartButton() {
    if (!this.bundleAddToCartBtn) return;

    // Always show the ATC button wrapper
    if (this.bundleAtcWrapper) {
      this.bundleAtcWrapper.style.display = 'block';
    }

    if (this.selectedProducts.size === 0) {
      // Disable button and hide share/price when no products selected
      this.bundleAddToCartBtn.disabled = true;
      this.bundleAddToCartBtn.style.opacity = '0.5';
      this.bundleAddToCartBtn.style.cursor = 'not-allowed';
      if (this.bundleShareBtn) {
        this.bundleShareBtn.style.display = 'none';
      }
      if (this.bundlePriceContainer) {
        this.bundlePriceContainer.style.display = 'none';
      }
      if (this.bundlePriceContainerMobile) {
        this.bundlePriceContainerMobile.style.display = 'none';
      }
      // Remove has-products class to hide mobile floating bar
      this.element.classList.remove('has-products');
      return;
    }

    // Add has-products class to show mobile floating bar
    this.element.classList.add('has-products');

    // Enable button when products are selected
    this.bundleAddToCartBtn.disabled = false;
    this.bundleAddToCartBtn.style.opacity = '1';
    this.bundleAddToCartBtn.style.cursor = 'pointer';

    // Show share button
    if (this.bundleShareBtn) {
      this.bundleShareBtn.style.display = 'flex';
    }

    const mainProductPrice = this.getMainProductPrice();
    const bundleProductsTotal = this.getBundleProductsTotal();
    const totalPrice = mainProductPrice + bundleProductsTotal;

    console.log('Price calculation:', {
      mainProductPrice,
      bundleProductsTotal,
      totalPrice,
      formattedTotal: this.formatMoney(totalPrice)
    });

    // Update price container above ATC button (desktop)
    if (this.bundlePriceContainer && this.bundleNowPrice) {
      this.bundlePriceContainer.style.display = 'block';
      this.bundleNowPrice.textContent = this.formatMoney(bundleProductsTotal);

      // Calculate and show compare-at price if any product has one
      const compareAtTotal = this.getBundleCompareAtTotal();
      if (this.bundleOldPrice) {
        if (compareAtTotal > bundleProductsTotal) {
          this.bundleOldPrice.textContent = this.formatMoney(compareAtTotal);
          this.bundleOldPrice.style.display = 'inline';
        } else {
          this.bundleOldPrice.style.display = 'none';
        }
      }
    }

    // Update mobile price container (outside floating bar)
    if (this.bundlePriceContainerMobile && this.bundleNowPriceMobile) {
      this.bundlePriceContainerMobile.style.display = 'block';
      this.bundleNowPriceMobile.textContent = this.formatMoney(bundleProductsTotal);

      const compareAtTotal = this.getBundleCompareAtTotal();
      if (this.bundleOldPriceMobile) {
        if (compareAtTotal > bundleProductsTotal) {
          this.bundleOldPriceMobile.textContent = this.formatMoney(compareAtTotal);
          this.bundleOldPriceMobile.style.display = 'inline';
        } else {
          this.bundleOldPriceMobile.style.display = 'none';
        }
      }
    }
  }

  getBundleCompareAtTotal() {
    let totalCents = 0;
    this.selectedProducts.forEach(item => {
      const quantity = item.quantity || 1;
      // Use compare_at_price if available, otherwise use regular price
      const priceValue = item.selectedVariant.compare_at_price || item.selectedVariant.price;
      let priceInCents;

      // Determine if price is in dollars (string with decimal) or cents
      if (typeof priceValue === 'string' && priceValue.includes('.')) {
        // Dollar format (e.g., "605.00") - convert to cents
        priceInCents = Math.round(parseFloat(priceValue) * 100);
      } else {
        // Already in cents (number like 60500 or string like "60500")
        priceInCents = parseInt(priceValue, 10) || 0;
      }

      totalCents += priceInCents * quantity;
    });
    return totalCents;
  }

  getCurrentVariantId() {
    // Find the main product's variant selector
    // Common selectors in Shopify themes:
    // 1. Hidden input with name="id" in the product form
    // 2. Select element with variant options
    // 3. Radio buttons or custom variant selectors

    // Try to find the product form (usually has action="/cart/add")
    const productForm = document.querySelector('form[action*="/cart/add"]');

    if (productForm) {
      // Look for variant ID input (most common case)
      const variantInput = productForm.querySelector('input[name="id"], select[name="id"], [name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
    }

    // Fallback: Look for variant selector outside form
    const variantSelector = document.querySelector('[data-product-variant-id], [data-variant-id]');
    if (variantSelector) {
      return variantSelector.value || variantSelector.dataset.productVariantId || variantSelector.dataset.variantId;
    }

    // Fallback: Check if there's a product JSON with current variant
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const productData = window.ShopifyAnalytics.meta.product;
      if (productData.variants && productData.variants.length > 0) {
        // Return first variant as fallback (usually the selected one)
        return productData.variants[0].id;
      }
    }

    return null;
  }

  /**
   * Save current selections to localStorage
   */
  saveToLocalStorage() {
    try {
      const storageKey = this.storageKey();
      const selectionsData = {
        selections: Array.from(this.selectedProducts.entries()).map(([key, value]) => ({
          key,
          sectionId: value.sectionId,
          product: value.product,
          selectedVariant: value.selectedVariant,
          quantity: value.quantity,
          userModified: this.userModifiedVariants.has(key)
        })),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(selectionsData));
      console.log('💾 Saved selections to localStorage:', selectionsData);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  /**
   * Load selections from localStorage
   */
  async loadFromLocalStorage() {
    try {
      const storageKey = this.storageKey();
      const savedData = localStorage.getItem(storageKey);

      if (!savedData) {
        console.log('📂 No saved selections found in localStorage');
        return;
      }

      const { selections, timestamp } = JSON.parse(savedData);
      console.log('📂 Loading selections from localStorage:', { selections, timestamp });

      // Wait for products to be loaded first
      await this.productsLoadedPromise;

      // Restore selections
      this.selectedProducts.clear();
      this.userModifiedVariants.clear();

      selections.forEach(item => {
        // NF 2026-10-12 (Sicherheit): Menge nur als ganze Zahl 1–99, Schlüssel/Bereich nur aus Wort-Zeichen –
        // beides landet später im HTML; ein manipulierter Teilen-Link schreibt über loadFromURL hierher
        const qty = this.nfSafeQty(item && item.quantity);
        if (!qty || !this.nfSafeKey(item.key) || !this.nfSafeKey(item.sectionId)) return;
        this.selectedProducts.set(item.key, {
          sectionId: item.sectionId,
          product: item.product,
          selectedVariant: item.selectedVariant,
          quantity: qty
        });

        if (item.userModified) {
          this.userModifiedVariants.add(item.key);
        }
      });

      // NF: Griffe, die zum Stil dieser Fronten nicht passen, fliegen raus (Hinweis) – auch aus dem Speicher
      if (this.enforceStyleRule().length) this.saveToLocalStorage();

      // Render all sections with saved selections
      this.sections.forEach(section => {
        this.renderSelectedProductsForSection(section.sectionId);
      });

      // Update section header summaries to show correct counts and totals
      this.updateSectionHeaderSummaries();

      // Update the ATC button state and price display
      this.updateBundleAddToCartButton();

      console.log('✅ Restored selections from localStorage');
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(this.storageKey());
      } catch (e) {
        console.error('Failed to clear corrupted localStorage:', e);
      }
    }
  }

  /**
   * Generate shareable URL with current bundle selections
   */
  generateShareableURL() {
    try {
      // Create compact data structure
      const shareData = {
        b: this.settings.blockId,
        s: Array.from(this.selectedProducts.entries()).map(([key, value]) => ({
          k: key, // compound key (sectionId_variantId)
          p: value.product.id,
          v: value.selectedVariant.id,
          q: value.quantity
        }))
      };

      // Encode to base64
      const jsonString = JSON.stringify(shareData);
      const base64 = btoa(jsonString);

      // Create URL with bundle parameter
      const url = new URL(window.location.href);
      url.searchParams.set('bundle', base64);

      console.log('📤 Generated shareable URL:', url.toString());
      return url.toString();
    } catch (error) {
      console.error('Failed to generate shareable URL:', error);
      return null;
    }
  }

  // NF 2026-10-12: Menge aus Link/Speicher → ganze Zahl 1–99, sonst 0 (= Eintrag verwerfen)
  nfSafeQty(q) {
    const n = typeof q === 'number' ? q : (typeof q === 'string' && /^\s*\d+\s*$/.test(q) ? parseInt(q, 10) : NaN);
    return Number.isFinite(n) && n >= 1 ? Math.min(99, Math.floor(n)) : 0;
  }

  // NF 2026-10-12: Schlüssel/Bereichs-ID aus Link/Speicher nur aus Wort-Zeichen und Bindestrich (section2_123_lw)
  nfSafeKey(k) {
    return typeof k === 'string' && /^[A-Za-z0-9_-]{1,120}$/.test(k);
  }

  /**
   * Load selections from URL parameter
   * Returns true if selections were loaded from URL
   */
  loadFromURL() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const bundleParam = urlParams.get('bundle');

      if (!bundleParam) {
        console.log('📥 No shared bundle in URL');
        return false;
      }

      console.log('📥 Loading shared bundle from URL...');

      // Decode from base64
      const jsonString = atob(bundleParam);
      const shareData = JSON.parse(jsonString);

      // Verify block ID matches
      if (this.stableBlockId(shareData.b) !== this.stableBlockId(this.settings.blockId)) {
        console.warn('⚠️ Shared bundle is for a different block, ignoring',
          { link: shareData.b, page: this.settings.blockId });
        return false;
      }

      // Wait for products to load, then restore selections
      // (NF: als _urlRestorePromise gemerkt – s. restorePromise in init)
      this._urlRestorePromise = this.productsLoadedPromise.then(() => {
        this.selectedProducts.clear();
        this.userModifiedVariants.clear();

        // NF 2026-10-05: nicht gefundene Positionen sammeln (statt still zu überspringen) – sonst meldet der
        // „Bearbeiten“-Link „geladen!“, und nfAddToCart (nf_replace) entfernt die fehlenden Zeilen aus dem Warenkorb.
        const missing = [];

        // Restore each selection
        shareData.s.forEach(item => {
          // Find the product and variant in our loaded data
          // Search every loaded pool - a shared bundle may contain items from a
          // section collection (e.g. handles), not just the main collection.
          const product = this.getAllLoadedProducts().find(p => p.id === item.p);
          if (!product) {
            console.warn('Product not found in any loaded collection:', item.p);
            missing.push(item);
            return;
          }

          const variant = product.variants.find(v => v.id === item.v);
          if (!variant) {
            console.warn('Variant not found:', item.v);
            missing.push(item);
            return;
          }

          // NF 2026-10-12 (Sicherheit): Schlüssel und Menge kommen ungeprüft aus dem Link und landen im HTML →
          // Schlüssel nur aus Wort-Zeichen/Bindestrich, Menge nur als ganze Zahl 1–99; sonst wie „nicht gefunden“
          const qty = this.nfSafeQty(item.q);
          if (!qty || !this.nfSafeKey(item.k)) {
            console.warn('Invalid shared item:', item.v);
            missing.push(item);
            return;
          }

          // Extract section ID from compound key
          const sectionId = item.k.split('_')[0];

          this.selectedProducts.set(item.k, {
            sectionId: sectionId,
            product: product,
            selectedVariant: variant,
            quantity: qty
          });
        });

        // NF: nicht erlaubte Griffe aus dem Link verwerfen (Hinweis folgt nach der Lade-Meldung)
        const styleRemoved = this.enforceStyleRule(true);

        // Render all sections with loaded selections
        this.sections.forEach(section => {
          this.renderSelectedProductsForSection(section.sectionId);
        });

        // ...and refresh the headers/prices. The localStorage path does this,
        // the shared-link path did not - the products appeared inside the
        // section while its header kept showing "0 Produkte | 0,00 EUR".
        this.updateSelectionSummary();
        this.updateSectionHeaderSummaries();
        this.updateBundleAddToCartButton();

        this._nfRestoreIncomplete = missing.length > 0;
        this._nfRestoreMissing = new Set(missing.map(m => String(m.v)));
        if (missing.length) {
          console.warn('⚠️ Shared bundle incomplete:', missing.length, 'of', shareData.s.length, 'items missing',
            this._nfLoadFailed ? Array.from(this._nfLoadFailed) : [], this._nfTruncated ? Array.from(this._nfTruncated) : []);
          // NF 2026-10-23: fehlende Positionen benennen (Front + Menge), nicht nur zählen
          this._nfRestoreMissingNames = missing.map(m => this.nfMissingItemName(m));
          this.showRestoreIncompleteNotice(missing.length, false, this._nfRestoreMissingNames);
        } else {
          console.log('✅ Loaded shared bundle with', shareData.s.length, 'items');
          // Show notification
          this.showNotification('Bundle aus geteiltem Link geladen!');
        }
        if (styleRemoved.length) this.showNotification(this.styleRuleRemovedMessage(styleRemoved), 6000);
      }).catch(error => {
        console.error('Failed to restore shared bundle:', error);
      });

      return true;
    } catch (error) {
      console.error('Failed to load from URL:', error);
      return false;
    }
  }

  /**
   * Show a temporary notification to the user
   */
  /**
   * NF 2026-10-05: Hinweis, wenn Positionen aus einem Link nicht geladen wurden.
   * blocked = true: „In den Warenkorb“ beim Bearbeiten (nf_replace) wurde gesperrt, damit nichts verloren geht.
   */
  showRestoreIncompleteNotice(count, blocked, names) {
    const n = Number(count) || 0;
    const what = n === 1 ? '1 Position konnte' : `${n} Positionen konnten`;
    // NF 2026-10-23: Positionen benennen (höchstens 6), Telefon als Ausweg
    const list = !blocked && Array.isArray(names) && names.length
      ? `: ${names.slice(0, 6).join(', ')}${names.length > 6 ? ` und ${names.length - 6} weitere` : ''}`
      : '';
    const msg = blocked
      ? `${what} nicht geladen werden. Damit sie nicht aus dem Warenkorb verschwinden, wird nicht gespeichert – bitte Seite neu laden.`
      : `${what} nicht geladen werden${list}. Bitte Seite neu laden oder anrufen: 0211 54208980.`;
    this.showNotification(msg, 20000, {
      error: true,
      tel: blocked ? null : { text: '0211 54208980', href: '+4921154208980' },
      action: { label: 'Erneut versuchen', onClick: () => location.reload() }
    });
  }

  /** NF 2026-10-23: Name einer nicht geladenen Position aus einem Teilen-/Bearbeiten-Link – „2× Obere Türen links“. */
  nfMissingItemName(item) {
    try {
      const id = item && item.p;
      const product = this.getAllLoadedProducts().find(p => p.id === id) ||
        (this._nfListById && this._nfListById.get(String(id)));
      const name = product ? this.getProductDisplayTitle(product) : 'Front';
      const qty = this.nfSafeQty(item && item.q);
      return qty > 1 ? `${qty}× ${name}` : name;
    } catch (e) {
      return 'Front';
    }
  }

  showNotification(message, duration, opts) {
    opts = opts || {};
    // Create notification element
    const notification = document.createElement('div');
    // NF: mehrere Hinweise gleichzeitig (geteilter Link + entfernte Griffe) untereinander statt übereinander
    let top = 20;
    document.querySelectorAll('.nf-bundle-note').forEach(n => {
      const r = n.getBoundingClientRect();
      top = Math.max(top, r.bottom + 10);
    });
    notification.className = 'nf-bundle-note';
    notification.setAttribute('role', opts.error ? 'alert' : 'status');
    notification.style.cssText = `
      position: fixed;
      top: ${top}px;
      right: 20px;
      max-width: min(420px, calc(100vw - 40px));
      box-sizing: border-box;
      background: #000;
      color: #fff;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 2147483646;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.45;
      animation: slideIn 0.3s ease-out;
    `;
    // NF 2026-10-23: opts.tel = { text, href } – diese Nummer im Text als tel:-Link
    const telAt = opts.tel && opts.tel.text ? String(message).indexOf(opts.tel.text) : -1;
    if (telAt >= 0) {
      notification.appendChild(document.createTextNode(String(message).slice(0, telAt)));
      const a = document.createElement('a');
      a.href = 'tel:' + opts.tel.href;
      a.textContent = opts.tel.text;
      [['color', '#fff'], ['text-decoration', 'underline'], ['white-space', 'nowrap']]
        .forEach(([k, v]) => a.style.setProperty(k, v, 'important'));
      notification.appendChild(a);
      notification.appendChild(document.createTextNode(String(message).slice(telAt + opts.tel.text.length)));
    } else {
      notification.textContent = message;
    }
    if (opts.action && opts.action.label) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = opts.action.label;
      btn.style.cssText = 'display:block;margin-top:10px;padding:8px 14px;background:#fff;color:#000;border:0;border-radius:6px;font:inherit;font-weight:600;cursor:pointer;';
      // NF 2026-10-06: Theme-CSS setzt bei Knöpfen float:left und margin 0 mit !important – sonst liegt der Knopf über dem Hinweistext
      [['float', 'none'], ['clear', 'both'], ['display', 'block'], ['margin', '10px 0 0'], ['width', 'auto'], ['position', 'static']]
        .forEach(([k, v]) => btn.style.setProperty(k, v, 'important'));
      btn.addEventListener('click', () => { try { opts.action.onClick && opts.action.onClick(); } catch (e) {} });
      notification.appendChild(btn);
    }

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(400px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Remove after 3 seconds (or the given duration)
    setTimeout(() => {
      notification.style.animation = 'slideIn 0.3s ease-out reverse';
      setTimeout(() => notification.remove(), 300);
    }, duration || 3000);
  }

  /**
   * Show animated toast when product is added to bundle
   */
  showProductAddedToast(product, variant, isNewProduct, quantity) {
    console.log('🎨 showProductAddedToast() called!');
    console.log('  Product:', product.title);
    console.log('  Is new product:', isNewProduct);
    console.log('  Quantity:', quantity);

    // Remove any existing toast
    const existingToast = document.querySelector('.bundle-product-added-toast');
    if (existingToast) {
      console.log('  Removing existing toast');
      existingToast.remove();
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'bundle-product-added-toast';
    toast.setAttribute('style', 'position: fixed !important; z-index: 999999999 !important;');

    const variantTitle = this.getVariantTitle(variant, product);
    const displayTitle = this.getProductDisplayTitle(product);
    const title = isNewProduct ? '✓ Hinzugefügt!' : '✓ Anzahl aktualisiert!';
    const message = isNewProduct
      ? `${displayTitle}${variantTitle ? ' - ' + variantTitle : ''}`
      : `${displayTitle} (×${quantity})`;

    toast.innerHTML = `
      <div class="bundle-product-added-toast-icon">✓</div>
      <div class="bundle-product-added-toast-content">
        <div class="bundle-product-added-toast-title">${title}</div>
        <div class="bundle-product-added-toast-message">${message}</div>
      </div>
    `;

    console.log('  Appending toast to body');
    document.body.appendChild(toast);
    console.log('  Toast element:', toast);
    console.log('  Toast computed style:', window.getComputedStyle(toast));

    // Trigger animation with delay
    setTimeout(() => {
      console.log('  Adding show class to toast');
      toast.classList.add('show');
    }, 50);

    // Auto-hide after 2.5 seconds
    setTimeout(() => {
      console.log('  Hiding toast');
      toast.classList.remove('show');
      toast.classList.add('hide');

      // Remove from DOM after animation
      setTimeout(() => {
        console.log('  Removing toast from DOM');
        if (toast.parentNode) {
          toast.remove();
        }
      }, 400);
    }, 2500);
  }

  getMainProductPrice() {
    // Try to get the currently selected variant's price from the form
    const variantId = this.getCurrentVariantId();

    if (variantId) {
      // Try to get from product JSON if available
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const productData = window.ShopifyAnalytics.meta.product;
        if (productData.variants) {
          const variant = productData.variants.find(v => v.id == variantId);
          if (variant && variant.price) {
            // Price is in cents
            return parseFloat(variant.price);
          }
        }
      }

      // Fallback: Try to find price from price display elements
      const priceSelectors = [
        '.price__sale .price-item--sale',
        '.product__price .price-item',
        '[data-product-price]',
        '.price-item--regular',
        '.product-price',
        '.price'
      ];

      for (const selector of priceSelectors) {
        const priceElement = document.querySelector(selector);
        if (priceElement) {
          const priceText = priceElement.textContent.trim();
          // Parse price from text like "$49.99" or "49.99" or "49,99"
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            const priceStr = priceMatch[0].replace(',', '.');
            const price = parseFloat(priceStr);
            // Assume it's in dollars, convert to cents
            return price * 100;
          }
        }
      }
    }

    // Fallback to 0 if we can't determine the price
    console.warn('Could not determine main product price');
    return 0;
  }

  getBundleProductsTotal() {
    let totalCents = 0;
    this.selectedProducts.forEach(item => {
      const quantity = item.quantity || 1;
      const priceValue = item.selectedVariant.price;
      let variantPriceInCents;

      // Determine if price is in dollars (string with decimal) or cents
      if (typeof priceValue === 'string' && priceValue.includes('.')) {
        // Dollar format (e.g., "605.00") - convert to cents
        variantPriceInCents = Math.round(parseFloat(priceValue) * 100);
      } else {
        // Already in cents (number like 60500 or string like "60500")
        variantPriceInCents = parseInt(priceValue, 10) || 0;
      }

      totalCents += variantPriceInCents * quantity;
    });
    return totalCents;
  }

  handleShareBundle() {
    if (this.selectedProducts.size === 0) {
      this.showNotification('Bitte wählen Sie Produkte zum Teilen aus');
      return;
    }

    const shareURL = this.generateShareableURL();
    if (!shareURL) {
      this.showNotification('Fehler beim Erstellen des teilbaren Links');
      return;
    }

    // Always show the share modal with options
    this.showShareModal(shareURL);
  }

  showShareModal(url) {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.2);
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 540px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;

    content.innerHTML = `
      <h3 style="margin: 0 0 8px; font-size: 22px; font-weight: 700;">Auswahl teilen</h3>
      <p style="margin: 0 0 20px; color: #666; font-size: 14px;">Teilen oder speichern Sie diese Konfiguration per Link oder E-Mail</p>

      <div>
        <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 8px;">Link:</label>
        <div style="display: flex; gap: 8px;">
          <input
            type="text"
            value="${url}"
            readonly
            class="share-url-input"
            style="flex: 1; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; font-family: monospace; background: #f9f9f9;"
            onclick="this.select()"
          />
          <button
            class="copy-link-btn"
            style="margin-bottom: 10px; padding: 12px 20px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; white-space: nowrap; transition: background 0.2s;"
            onmouseover="this.style.background='#333'"
            onmouseout="this.style.background='#000'"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="vertical-align: middle; margin-right: 6px;">
              <path d="M5 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H11C11.5523 14 12 13.5523 12 13V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <rect x="6" y="2" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            Kopieren
          </button>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 10px;">
        <button
          class="contact-advisor-btn"
          style="margin: 0; padding: 14px 20px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;"
          onmouseover="this.style.background='#333'"
          onmouseout="this.style.background='#000'"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          Berater kontaktieren
        </button>

        <button
          class="send-to-self-btn"
          style="margin: 0; padding: 14px 20px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;"
          onmouseover="this.style.background='#333'"
          onmouseout="this.style.background='#000'"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          An mich schicken
        </button>
      </div>

      <button
        class="close-modal-btn"
        style="width: 100%; padding: 14px 20px; background: #f5f5f5; color: #333; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
        onmouseover="this.style.background='#e5e5e5'"
        onmouseout="this.style.background='#f5f5f5'"
      >
        Schließen
      </button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Copy link button handler
    const copyBtn = content.querySelector('.copy-link-btn');
    const urlInput = content.querySelector('.share-url-input');
    copyBtn.addEventListener('click', () => {
      urlInput.select();

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url)
          .then(() => {
            copyBtn.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="vertical-align: middle; margin-right: 6px;">
                <path d="M13 4L6 11L3 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Kopiert!
            `;
            setTimeout(() => {
              copyBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="vertical-align: middle; margin-right: 6px;">
                  <path d="M5 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H11C11.5523 14 12 13.5523 12 13V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <rect x="6" y="2" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                Kopieren
              `;
            }, 2000);
          })
          .catch(err => {
            console.error('Failed to copy:', err);
            this.showNotification('Bitte Link manuell kopieren');
          });
      } else {
        // Fallback: just select the text
        this.showNotification('Link ausgewählt - drücken Sie Strg+C zum Kopieren');
      }
    });

    // Contact advisor button handler
    const contactAdvisorBtn = content.querySelector('.contact-advisor-btn');
    contactAdvisorBtn.addEventListener('click', () => {
      modal.remove();
      this.showContactAdvisorForm(url);
    });

    // Send to self button handler
    const sendToSelfBtn = content.querySelector('.send-to-self-btn');
    sendToSelfBtn.addEventListener('click', () => {
      modal.remove();
      this.showSendToSelfForm(url);
    });

    // Close button handler
    const closeBtn = content.querySelector('.close-modal-btn');
    closeBtn.addEventListener('click', () => {
      modal.remove();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    // Select the URL text on load
    setTimeout(() => {
      urlInput.select();
      urlInput.focus();
    }, 100);
  }

  generateRecoveryCode() {
    // Generate a memorable code like "MXKT-4829"
    // Avoid confusing characters: 0/O, 1/I/L
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ';
    const nums = '23456789';

    let code = '';
    // 4 letters
    for (let i = 0; i < 4; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    code += '-';
    // 4 numbers
    for (let i = 0; i < 4; i++) {
      code += nums.charAt(Math.floor(Math.random() * nums.length));
    }
    return code;
  }

  saveWithRecoveryCode(bundleUrl, shareModal) {
    // Generate a unique recovery code
    const code = this.generateRecoveryCode();

    // Create bundle save data
    const bundleData = {
      code: code,
      url: bundleUrl,
      productUrl: window.location.pathname,
      productTitle: document.querySelector('h1')?.textContent?.trim() || 'Bundle',
      savedAt: new Date().toISOString(),
      selections: this.getSelectionsForSave()
    };

    // Save to localStorage with the code as key
    try {
      localStorage.setItem(`bundleCode_${code}`, JSON.stringify(bundleData));
    } catch (e) {
      console.warn('Could not save bundle with code:', e);
      this.showNotification('Fehler beim Speichern');
      return;
    }

    // Close the share modal
    if (shareModal) {
      shareModal.remove();
    }

    // Show the recovery code dialog
    this.showRecoveryCodeDialog(code);
  }

  showRecoveryCodeDialog(code) {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.5);
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 400px;
      width: 100%;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;

    content.innerHTML = `
      <div style="width: 64px; height: 64px; background: #e8f5e9; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4caf50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <h3 style="margin: 0 0 8px; font-size: 22px; font-weight: 700;">Konfiguration gespeichert!</h3>
      <p style="margin: 0 0 24px; color: #666; font-size: 14px;">Notieren Sie sich diesen Code, um Ihre Auswahl später wiederherzustellen:</p>

      <div style="background: #f5f5f5; border: 2px dashed #ccc; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <div class="recovery-code" style="font-family: monospace; font-size: 32px; font-weight: 700; letter-spacing: 4px; color: #000; user-select: all;">${code}</div>
      </div>

      <button
        class="copy-code-btn"
        style="width: 100%; margin-bottom: 10px; padding: 14px 20px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
        onmouseover="this.style.background='#333'"
        onmouseout="this.style.background='#000'"
      >
        Code kopieren
      </button>

      <button
        class="close-code-modal-btn"
        style="width: 100%; padding: 14px 20px; background: #f5f5f5; color: #333; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
        onmouseover="this.style.background='#e5e5e5'"
        onmouseout="this.style.background='#f5f5f5'"
      >
        Schließen
      </button>

      <p style="margin: 16px 0 0; color: #999; font-size: 12px;">Dieser Code ist 30 Tage gültig.</p>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Copy code button handler
    const copyBtn = content.querySelector('.copy-code-btn');
    copyBtn.addEventListener('click', () => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code)
          .then(() => {
            copyBtn.textContent = 'Kopiert!';
            setTimeout(() => {
              copyBtn.textContent = 'Code kopieren';
            }, 2000);
          })
          .catch(() => {
            this.showNotification('Bitte Code manuell kopieren');
          });
      }
    });

    // Close button handler
    const closeBtn = content.querySelector('.close-code-modal-btn');
    closeBtn.addEventListener('click', () => {
      modal.remove();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  showRestoreFromCodeDialog() {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.5);
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 400px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;

    content.innerHTML = `
      <h3 style="margin: 0 0 8px; font-size: 22px; font-weight: 700;">Code eingeben</h3>
      <p style="margin: 0 0 20px; color: #666; font-size: 14px;">Geben Sie Ihren Wiederherstellungscode ein:</p>

      <input
        type="text"
        class="recovery-code-input"
        placeholder="XXXX-0000"
        maxlength="9"
        style="width: 100%; padding: 16px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 24px; font-family: monospace; text-align: center; text-transform: uppercase; letter-spacing: 4px; box-sizing: border-box; margin-bottom: 20px;"
      />

      <div class="error-message" style="display: none; color: #dc3545; font-size: 14px; margin-bottom: 16px; text-align: center;"></div>

      <button
        class="restore-btn"
        style="width: 100%; margin-bottom: 10px; padding: 14px 20px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
        onmouseover="this.style.background='#333'"
        onmouseout="this.style.background='#000'"
      >
        Wiederherstellen
      </button>

      <button
        class="cancel-restore-btn"
        style="width: 100%; padding: 14px 20px; background: #f5f5f5; color: #333; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
        onmouseover="this.style.background='#e5e5e5'"
        onmouseout="this.style.background='#f5f5f5'"
      >
        Abbrechen
      </button>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    const input = content.querySelector('.recovery-code-input');
    const errorMsg = content.querySelector('.error-message');
    const restoreBtn = content.querySelector('.restore-btn');
    const cancelBtn = content.querySelector('.cancel-restore-btn');

    // Auto-format input (add dash after 4 characters)
    input.addEventListener('input', (e) => {
      let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (value.length > 4) {
        value = value.slice(0, 4) + '-' + value.slice(4, 8);
      }
      e.target.value = value;
      errorMsg.style.display = 'none';
    });

    // Restore button handler
    restoreBtn.addEventListener('click', () => {
      const code = input.value.toUpperCase().trim();

      if (code.length !== 9) {
        errorMsg.textContent = 'Bitte geben Sie einen gültigen Code ein (z.B. ABCD-1234)';
        errorMsg.style.display = 'block';
        return;
      }

      // Try to load the bundle
      try {
        const savedData = localStorage.getItem(`bundleCode_${code}`);
        if (!savedData) {
          errorMsg.textContent = 'Code nicht gefunden. Bitte überprüfen Sie Ihre Eingabe.';
          errorMsg.style.display = 'block';
          return;
        }

        const bundleData = JSON.parse(savedData);

        // Check if code is expired (30 days)
        const savedDate = new Date(bundleData.savedAt);
        const now = new Date();
        const daysDiff = (now - savedDate) / (1000 * 60 * 60 * 24);

        if (daysDiff > 30) {
          errorMsg.textContent = 'Dieser Code ist abgelaufen.';
          errorMsg.style.display = 'block';
          localStorage.removeItem(`bundleCode_${code}`);
          return;
        }

        // Redirect to the saved URL
        modal.remove();
        this.showNotification('Konfiguration wird geladen...');

        setTimeout(() => {
          window.location.href = bundleData.url;
        }, 500);

      } catch (e) {
        console.warn('Could not restore bundle:', e);
        errorMsg.textContent = 'Fehler beim Wiederherstellen.';
        errorMsg.style.display = 'block';
      }
    });

    // Cancel button handler
    cancelBtn.addEventListener('click', () => {
      modal.remove();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    // Focus input
    setTimeout(() => input.focus(), 100);
  }

  getSelectionsForSave() {
    // Get current selections in a saveable format
    const selections = [];
    this.selectedProducts.forEach((item, key) => {
      selections.push({
        sectionId: item.sectionId,
        productId: item.product.id,
        productTitle: item.product.title,
        variantId: item.selectedVariant.id,
        variantTitle: item.selectedVariant.title
      });
    });
    return selections;
  }

  showContactAdvisorForm(bundleUrl) {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.2);
      z-index: 2147483647;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
      overflow-y: auto;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 600px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      margin: 60px 0 20px 0;
    `;

    content.innerHTML = `
      <h3 style="margin: 0 0 8px; font-size: 22px; font-weight: 700;">Berater kontaktieren</h3>
      <p style="margin: 0 0 24px; color: #666; font-size: 14px;">Besprechen Sie Ihre Auswahl mit einem Berater und erhalten Sie persönliche Empfehlungen</p>

      <form class="advisor-contact-form" style="display: flex; flex-direction: column; gap: 16px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
          <div>
            <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
              Vorname <span style="color: #e53e3e;">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              required
              style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; transition: border-color 0.2s;"
              onfocus="this.style.borderColor='#000'"
              onblur="this.style.borderColor='#e5e5e5'"
            />
          </div>
          <div>
            <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
              Nachname <span style="color: #e53e3e;">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              required
              style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; transition: border-color 0.2s;"
              onfocus="this.style.borderColor='#000'"
              onblur="this.style.borderColor='#e5e5e5'"
            />
          </div>
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            E-Mail-Adresse <span style="color: #e53e3e;">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; transition: border-color 0.2s;"
            onfocus="this.style.borderColor='#000'"
            onblur="this.style.borderColor='#e5e5e5'"
          />
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            Telefonnummer <span style="color: #e53e3e;">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            required
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; transition: border-color 0.2s;"
            onfocus="this.style.borderColor='#000'"
            onblur="this.style.borderColor='#e5e5e5'"
          />
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            Bundle-Konfiguration
          </label>
          <input
            type="text"
            name="bundleUrlDisplay"
            value="${bundleUrl}"
            readonly
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 13px; background: #f9f9f9; color: #666; font-family: monospace; cursor: default;"
            onclick="this.select()"
          />
          <p style="margin: 6px 0 0; font-size: 12px; color: #666;">Diese Bundle-Auswahl wird automatisch an den Berater gesendet</p>
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            Nachricht (optional)
          </label>
          <textarea
            name="message"
            rows="4"
            placeholder="Teilen Sie uns Ihre Wünsche oder Fragen mit..."
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; resize: vertical; font-family: inherit; transition: border-color 0.2s;"
            onfocus="this.style.borderColor='#000'"
            onblur="this.style.borderColor='#e5e5e5'"
          ></textarea>
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            Dateien anhängen (optional)
          </label>
          <input
            type="file"
            name="files"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; background: #f9f9f9; cursor: pointer;"
          />
          <p style="margin: 6px 0 0; font-size: 12px; color: #999;">Max. Dateigröße: 10 MB pro Datei</p>
        </div>

        <input type="hidden" name="bundleUrl" value="${bundleUrl}" />

        <div class="form-error" style="display: none; padding: 12px; background: #fee; border: 1px solid #fcc; border-radius: 6px; color: #c33; font-size: 14px;"></div>

        <div style="display: grid; grid-template-columns: 1fr auto; gap: 12px; margin-top: 8px;">
          <button
            type="submit"
            class="submit-contact-btn"
            style="padding: 14px 24px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
            onmouseover="this.style.background='#333'"
            onmouseout="this.style.background='#000'"
          >
            Anfrage senden
          </button>
          <button
            type="button"
            class="cancel-contact-btn"
            style="padding: 14px 24px; background: #f5f5f5; color: #333; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
            onmouseover="this.style.background='#e5e5e5'"
            onmouseout="this.style.background='#f5f5f5'"
          >
            Abbrechen
          </button>
        </div>
      </form>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    const form = content.querySelector('.advisor-contact-form');
    const submitBtn = content.querySelector('.submit-contact-btn');
    const cancelBtn = content.querySelector('.cancel-contact-btn');
    const errorDiv = content.querySelector('.form-error');

    // Form submission handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Disable submit button
      submitBtn.disabled = true;
      submitBtn.textContent = 'Wird gesendet...';
      errorDiv.style.display = 'none';

      try {
        const formData = new FormData(form);
        const firstName = formData.get('firstName');
        const lastName = formData.get('lastName');
        const email = formData.get('email');
        const phone = formData.get('phone');
        const message = formData.get('message') || '';
        const bundleUrlValue = formData.get('bundleUrl');

        // Build the email body
        const emailBody = `
Neue Bundle-Beratungsanfrage

Kunde:
- Name: ${firstName} ${lastName}
- E-Mail: ${email}
- Telefon: ${phone}

Bundle-Konfiguration:
${bundleUrlValue}

Nachricht:
${message || '(Keine Nachricht)'}

---
Gesendet über das Bundle-Konfigurator Formular
        `.trim();

        // Submit to Formspree
        const formspreeData = new FormData();
        formspreeData.append('name', `${firstName} ${lastName}`);
        formspreeData.append('email', email);
        formspreeData.append('phone', phone);
        formspreeData.append('message', emailBody);
        formspreeData.append('_subject', 'Neue Bundle-Beratungsanfrage');

        // Add file attachments if any
        const fileInput = form.querySelector('input[type="file"]');
        if (fileInput && fileInput.files.length > 0) {
          for (let i = 0; i < fileInput.files.length; i++) {
            formspreeData.append('attachment', fileInput.files[i]);
          }
        }

        const response = await fetch('https://usebasin.com/f/ec9db4b351f5', {
          method: 'POST',
          body: formspreeData,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Formspree error:', errorData);
          throw new Error('Fehler beim Senden');
        }

        // Success
        modal.remove();
        this.showNotification('Anfrage erfolgreich gesendet! Wir melden uns in Kürze bei Ihnen.');

      } catch (error) {
        console.error('Form submission error:', error);
        errorDiv.textContent = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
        errorDiv.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Anfrage senden';
      }
    });

    // Cancel button handler
    cancelBtn.addEventListener('click', () => {
      modal.remove();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  showSendToSelfForm(bundleUrl) {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.2);
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
      overflow-y: auto;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 480px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      margin: 20px 0;
    `;

    content.innerHTML = `
      <h3 style="margin: 0 0 8px; font-size: 22px; font-weight: 700;">An mich schicken</h3>
      <p style="margin: 0 0 24px; color: #666; font-size: 14px;">Erhalten Sie diese Bundle-Konfiguration per E-Mail</p>

      <form class="send-to-self-form" style="display: flex; flex-direction: column; gap: 16px;">
        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            E-Mail-Adresse <span style="color: #e53e3e;">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="ihre@email.de"
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 14px; transition: border-color 0.2s;"
            onfocus="this.style.borderColor='#000'"
            onblur="this.style.borderColor='#e5e5e5'"
          />
        </div>

        <div>
          <label style="display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px;">
            Bundle-Konfiguration
          </label>
          <input
            type="text"
            name="bundleUrlDisplay"
            value="${bundleUrl}"
            readonly
            style="width: 100%; padding: 12px 14px; border: 2px solid #e5e5e5; border-radius: 6px; font-size: 13px; background: #f9f9f9; color: #666; font-family: monospace; cursor: default;"
            onclick="this.select()"
          />
          <p style="margin: 6px 0 0; font-size: 12px; color: #666;">Dieser Link wird an Ihre E-Mail-Adresse gesendet</p>
        </div>

        <input type="hidden" name="bundleUrl" value="${bundleUrl}" />

        <div class="form-error" style="display: none; padding: 12px; background: #fee; border: 1px solid #fcc; border-radius: 6px; color: #c33; font-size: 14px;"></div>

        <div style="display: grid; grid-template-columns: 1fr auto; gap: 12px; margin-top: 8px;">
          <button
            type="submit"
            class="submit-send-btn"
            style="padding: 14px 24px; background: #000; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;"
            onmouseover="this.style.background='#333'"
            onmouseout="this.style.background='#000'"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
            Senden
          </button>
          <button
            type="button"
            class="cancel-send-btn"
            style="padding: 14px 24px; background: #f5f5f5; color: #333; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px; transition: background 0.2s;"
            onmouseover="this.style.background='#e5e5e5'"
            onmouseout="this.style.background='#f5f5f5'"
          >
            Abbrechen
          </button>
        </div>
      </form>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    const form = content.querySelector('.send-to-self-form');
    const submitBtn = content.querySelector('.submit-send-btn');
    const cancelBtn = content.querySelector('.cancel-send-btn');
    const errorDiv = content.querySelector('.form-error');

    // Form submission handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Disable submit button
      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite;">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-opacity="1"></path>
        </svg>
        Wird gesendet...
      `;
      errorDiv.style.display = 'none';

      try {
        const formData = new FormData(form);
        const email = formData.get('email');
        const bundleUrlValue = formData.get('bundleUrl');

        // Build email content
        const subject = encodeURIComponent('Meine Bundle-Konfiguration');
        const body = encodeURIComponent(`Hallo,

hier ist meine gespeicherte Bundle-Konfiguration:

${bundleUrlValue}

Klicken Sie auf den Link, um die Konfiguration zu öffnen.

Viele Grüße`);

        // Open mailto link
        const mailtoUrl = `mailto:${email}?subject=${subject}&body=${body}`;
        window.location.href = mailtoUrl;

        // Close modal after a short delay
        setTimeout(() => {
          modal.remove();
          this.showNotification('E-Mail-Programm geöffnet - bitte senden Sie die E-Mail ab.');
        }, 500);

      } catch (error) {
        console.error('Send to self error:', error);
        errorDiv.textContent = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
        errorDiv.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
          Senden
        `;
      }
    });

    // Cancel button handler
    cancelBtn.addEventListener('click', () => {
      modal.remove();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    // Focus email input
    setTimeout(() => {
      const emailInput = content.querySelector('input[name="email"]');
      if (emailInput) emailInput.focus();
    }, 100);
  }

  async handleBundleAddToCart() {
    // Try multiple selectors to find the main product form
    const formSelectors = [
      'form[action*="/cart/add"]',
      'form[action="/cart/add"]',
      'form.product-form',
      'form.shopify-product-form',
      '[data-product-form]',
      'form#product-form',
      'form#AddToCartForm'
    ];

    let productForm = null;
    for (const selector of formSelectors) {
      productForm = document.querySelector(selector);
      if (productForm) {
        console.log('Found product form with selector:', selector);
        break;
      }
    }

    if (productForm) {
      await this.addBundleToCart(productForm);
    } else {
      // No product form found - add only bundle products without main product
      console.warn('No product form found, adding bundle products only');
      await this.addBundleProductsOnly();
    }
  }

  async addBundleProductsOnly() {
    try {
      // NF: nie einen Griff in den Warenkorb legen, der zum Stil nicht passt
      if (this.enforceStyleRule().length) this.refreshAfterStyleRule();
      if (this.selectedProducts.size === 0) {
        alert('Keine Produkte ausgewählt.');
        return;
      }

      console.log('=== ADDING BUNDLE PRODUCTS ONLY ===');
      console.log('Number of bundle products:', this.selectedProducts.size);

      // Get current cart to determine box number
      let boxNumber = 1;
      try {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        const existingBundles = new Set();
        cartData.items.forEach(item => {
          if (item.properties && item.properties._bundle_id) {
            existingBundles.add(item.properties._bundle_id);
          }
        });
        boxNumber = existingBundles.size + 1;
      } catch (error) {
        console.warn('Could not fetch cart for box numbering:', error);
      }

      // Generate unique bundle ID
      const timestamp = Date.now();
      const randomNum = Math.floor(Math.random() * 99999) + 10000;
      const bundleId = `bundle_${timestamp}_${randomNum}`;
      const offerNumber = `#${randomNum}`;

      // Calculate total
      const bundleTotalCents = this.getBundleProductsTotal();
      const bundleTotalFormatted = this.formatMoney(bundleTotalCents);
      const itemCount = this.selectedProducts.size;

      // Prepare items
      const items = [];
      let componentIndex = 1;

      this.selectedProducts.forEach(item => {
        const itemProperties = {
          '_bundle_id': bundleId,
          '_bundle_component': 'true',
          '_component_index': componentIndex.toString(),
          '_box_number': boxNumber.toString(),
          // Nur intern (Felix 13.10.2026: Kunden sehen Bundle/Offer ID nicht mehr)
          '_Bundle': `Bundle ${offerNumber} - Box ${boxNumber}`,
          '_Offer ID': offerNumber
        };

        // Add background color from main product if selected
        if (this.selectedBackgroundFilter) {
          itemProperties['_background_color'] = this.selectedBackgroundFilter;
          itemProperties['Hintergrund Farbe'] = this.selectedBackgroundFilter;
        }

        items.push({
          id: parseInt(item.selectedVariant.id),
          quantity: item.quantity || 1,
          properties: itemProperties
        });
        componentIndex++;
      });

      console.log('Adding items:', items);

      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Fehler beim Hinzufügen zum Warenkorb');
      }

      console.log('✓ Successfully added bundle products to cart!');

      // First, trigger cart refresh events BEFORE clearing selections
      console.log('🔄 Triggering cart refresh events...');
      await this.refreshCartDrawer();

      // Clear selection and localStorage after successful cart addition
      this.selectedProducts.clear();
      this.userModifiedVariants.clear();
      localStorage.removeItem(this.storageKey());
      console.log('🗑️ Cleared selections and localStorage after successful cart addition');

      // Update all sections to remove selected items from display
      this.sections.forEach(section => {
        this.renderSelectedProductsForSection(section.sectionId);
      });
      this.updateSectionHeaderSummaries();
      this.updateBundleAddToCartButton();

      // Show success message
      this.showNotification('Bundle wurde zum Warenkorb hinzugefügt!');

      // Wait a moment for cart to update, then try to open drawer
      await new Promise(resolve => setTimeout(resolve, 300));

      // Try to open drawer cart instead of redirecting
      const drawerOpened = await this.openDrawerCart();

      if (drawerOpened) {
        // Refresh cart drawer content AFTER opening it
        // This ensures the drawer shows fresh content even if the theme re-renders on open
        await new Promise(resolve => setTimeout(resolve, 200));
        await this.refreshCartDrawer();
      } else {
        // Fallback: Redirect to cart page if no drawer cart found
        console.log('No drawer cart found, redirecting to cart page...');
        window.location.href = '/cart';
      }

    } catch (error) {
      console.error('Error adding bundle products:', error);
      alert('Fehler beim Hinzufügen zum Warenkorb: ' + error.message);
    }
  }

  getVariantTitle(variant, product) {
    const parts = [];
    if (variant.option1 && product.options[0]) {
      parts.push(variant.option1);
    }
    if (variant.option2 && product.options[1]) {
      parts.push(variant.option2);
    }
    if (variant.option3 && product.options[2]) {
      parts.push(variant.option3);
    }
    return parts.join(' / ');
  }

  // Extract color value from variant options
  getVariantColor(variant, product) {
    // Check each option to see if it's a color option
    const colorNames = ['farbe', 'color', 'colour', 'couleur'];

    for (let i = 0; i < product.options.length; i++) {
      const optionName = this.getOptionName(product.options[i]);
      if (optionName && colorNames.includes(optionName.toLowerCase())) {
        // Found the color option, return the corresponding value
        if (i === 0) return variant.option1;
        if (i === 1) return variant.option2;
        if (i === 2) return variant.option3;
      }
    }
    return null;
  }

  // Map color names to hex codes
  getColorHex(colorName) {
    if (!colorName) return null;

    const colorMap = {
      // Basic colors
      'schwarz': '#000000',
      'black': '#000000',
      'noir': '#000000',
      'weiss': '#FFFFFF',
      'weiß': '#FFFFFF',
      'white': '#FFFFFF',
      'blanc': '#FFFFFF',
      'rot': '#FF0000',
      'red': '#FF0000',
      'rouge': '#FF0000',
      'blau': '#0000FF',
      'blue': '#0000FF',
      'bleu': '#0000FF',
      'grün': '#00FF00',
      'gruen': '#00FF00',
      'green': '#00FF00',
      'vert': '#00FF00',
      'gelb': '#FFFF00',
      'yellow': '#FFFF00',
      'jaune': '#FFFF00',
      'orange': '#FFA500',
      'rosa': '#FFC0CB',
      'pink': '#FFC0CB',
      'rose': '#FFC0CB',
      'lila': '#800080',
      'purple': '#800080',
      'violet': '#800080',
      'braun': '#8B4513',
      'brown': '#8B4513',
      'brun': '#8B4513',
      'grau': '#808080',
      'gray': '#808080',
      'grey': '#808080',
      'gris': '#808080',
      'beige': '#F5F5DC',
      'navy': '#000080',
      'marine': '#000080',
      'türkis': '#40E0D0',
      'turquoise': '#40E0D0',
      'mint': '#98FF98',
      'khaki': '#F0E68C',
      'olive': '#808000',
      'gold': '#FFD700',
      'silber': '#C0C0C0',
      'silver': '#C0C0C0',
      'argent': '#C0C0C0'
    };

    const lowerColor = colorName.toLowerCase().trim();
    return colorMap[lowerColor] || null;
  }

  // Generate swatch HTML
  getSwatchHtml(colorName) {
    const hexColor = this.getColorHex(colorName);
    if (!hexColor) return '';

    const isLight = this.isLightColor(hexColor);
    const borderStyle = isLight ? 'border: 1px solid #ddd;' : '';

    return `<span class="bundle-color-swatch" style="background-color: ${hexColor}; ${borderStyle}" title="${colorName}"></span>`;
  }

  // Check if a color is light (for border)
  isLightColor(hex) {
    const rgb = parseInt(hex.substring(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma > 200;
  }

  interceptAddToCart() {
    // Find the main product form
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');

    productForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        if (this.selectedProducts.size > 0) {
          e.preventDefault();
          await this.addBundleToCart(form);
        }
      });
    });
  }

  async addBundleToCart(mainForm) {
    try {
      // NF: nie einen Griff in den Warenkorb legen, der zum Stil nicht passt
      if (this.enforceStyleRule().length) this.refreshAfterStyleRule();
      const formData = new FormData(mainForm);
      const mainVariantId = formData.get('id');
      const mainQuantity = parseInt(formData.get('quantity')) || 1;

      if (!mainVariantId) {
        throw new Error('No variant ID found in form');
      }

      console.log('=== ADDING BUNDLE TO CART ===');
      console.log('Main variant ID:', mainVariantId, 'Quantity:', mainQuantity);
      console.log('Number of bundle products:', this.selectedProducts.size);

      // Get current cart to determine box number
      let boxNumber = 1;
      try {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        // Count existing bundles in cart
        const existingBundles = new Set();
        cartData.items.forEach(item => {
          if (item.properties && item.properties._bundle_id) {
            existingBundles.add(item.properties._bundle_id);
          }
        });
        boxNumber = existingBundles.size + 1;
        console.log('Existing bundles in cart:', existingBundles.size, 'New box number:', boxNumber);
      } catch (error) {
        console.warn('Could not fetch cart for box numbering:', error);
      }

      // Generate unique bundle/offer ID
      const timestamp = Date.now();
      const randomNum = Math.floor(Math.random() * 99999) + 10000; // 5 digit random number
      const bundleId = `bundle_${timestamp}_${randomNum}`;
      const offerNumber = `#${randomNum}`;

      // Get main product title for "Part of" display
      let mainProductTitle = 'Bundle';
      try {
        // Priority 1: Try to get from Shopify Analytics (most reliable)
        if (window.ShopifyAnalytics?.meta?.product?.name) {
          mainProductTitle = window.ShopifyAnalytics.meta.product.name;
        }
        // Priority 2: Try to get from product JSON (if available)
        else if (window.product?.title) {
          mainProductTitle = window.product.title;
        }
        // Priority 3: Try to get from meta tags
        else {
          const metaProduct = document.querySelector('meta[property="og:title"]');
          if (metaProduct && metaProduct.content) {
            mainProductTitle = metaProduct.content;
          }
          // Priority 4: Fall back to DOM element (but only get the direct text, not child elements)
          else {
            const productTitleElement = document.querySelector('.product__title, [data-product-title], h1.product-title, .product-single__title');
            if (productTitleElement) {
              // Get only the direct text nodes, not nested elements that might contain metafields
              const textNodes = Array.from(productTitleElement.childNodes)
                .filter(node => node.nodeType === Node.TEXT_NODE)
                .map(node => node.textContent.trim())
                .filter(text => text.length > 0);

              if (textNodes.length > 0) {
                mainProductTitle = textNodes.join(' ').trim();
              } else {
                // If no text nodes, fall back to full text content
                mainProductTitle = productTitleElement.textContent.trim();
              }
            }
          }
        }
      } catch (error) {
        console.warn('Could not get main product title:', error);
      }

      console.log('Bundle details:', { bundleId, offerNumber, boxNumber, mainProductTitle });

      // Calculate total bundle price
      const bundleTotalCents = this.getBundleProductsTotal();
      const bundleTotalFormatted = this.formatMoney(bundleTotalCents);
      const itemCount = this.selectedProducts.size;

      console.log('Bundle total price:', bundleTotalFormatted, '(', bundleTotalCents, 'cents )');

      // Prepare cart items with bundle properties
      const mainProductProperties = {
        // Hidden properties (admin only - start with underscore)
        '_bundle_id': bundleId,
        '_bundle_lead': 'true',
        '_bundle_contains': `${itemCount} item(s)`,
        '_box_number': boxNumber.toString(),
        '_bundle_total_cents': bundleTotalCents.toString(),
        // Bundle-/Angebotsdaten nur intern (Felix 13.10.2026: Kunden sehen Bundle/Offer ID nicht mehr)
        '_Bundle': `Bundle ${offerNumber} - Box ${boxNumber}`,
        '_Bundle Total': `${bundleTotalFormatted} (${itemCount} item${itemCount !== 1 ? 's' : ''})`,
        '_Offer ID': offerNumber
      };

      // Add background color if selected on main product
      if (this.selectedBackgroundFilter) {
        mainProductProperties['_background_color'] = this.selectedBackgroundFilter;
        mainProductProperties['Hintergrund Farbe'] = this.selectedBackgroundFilter;
        console.log('Adding background color to main product:', this.selectedBackgroundFilter);
      }

      const items = [
        {
          id: parseInt(mainVariantId),
          quantity: mainQuantity,
          properties: mainProductProperties
        }
      ];

      // Add bundle products as components
      let componentIndex = 1;
      this.selectedProducts.forEach(item => {
        console.log('Adding bundle product:', {
          variantId: item.selectedVariant.id,
          quantity: item.quantity,
          product: item.product.title
        });

        const variantTitle = this.getVariantTitle(item.selectedVariant, item.product);

        const componentProperties = {
          // Hidden properties (admin only)
          '_bundle_id': bundleId,
          '_bundle_component': 'true',
          '_component_index': componentIndex.toString(),
          '_box_number': boxNumber.toString(),
          // Visible properties (customer-facing)
          'Gehört zu': mainProductTitle,
          // Bundle-/Angebotsnummer nur intern (Felix 13.10.2026)
          '_Bundle': `Bundle ${offerNumber} - Box ${boxNumber}`,
          '_Angebots-Nr.': offerNumber
        };

        // Add background color from main product if selected
        if (this.selectedBackgroundFilter) {
          componentProperties['_background_color'] = this.selectedBackgroundFilter;
          componentProperties['Hintergrund Farbe'] = this.selectedBackgroundFilter;
        }

        items.push({
          id: parseInt(item.selectedVariant.id),
          quantity: item.quantity,
          properties: componentProperties
        });
        componentIndex++;
      });

      console.log('Final cart payload:', JSON.stringify({ items }, null, 2));

      // Try batch add first
      console.log('Attempting batch add to cart...');
      let success = false;
      let batchError = null;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items })
        });

        console.log('Cart API response status:', response.status);

        let responseData;
        const responseText = await response.text();
        console.log('Cart API response text:', responseText);

        try {
          responseData = JSON.parse(responseText);
          console.log('Cart API response data:', responseData);
        } catch (e) {
          console.error('Failed to parse cart response:', e);
          throw new Error('Invalid response from cart API');
        }

        if (response.ok) {
          console.log('✓ Successfully added bundle to cart via batch add!');
          success = true;
        } else {
          batchError = responseData.description || responseData.message || responseData.error || 'Fehler beim Hinzufügen zum Warenkorb';
          console.warn('Batch add failed:', batchError);
          console.log('Will try adding items one by one...');
        }
      } catch (error) {
        batchError = error.message;
        console.warn('Batch add threw error:', error);
        console.log('Will try adding items one by one...');
      }

      // If batch add failed, try adding items one by one.
      // The batch call is atomic; this loop is not, so remember what the cart
      // looked like beforehand and undo our own lines if it breaks halfway -
      // otherwise a sold-out handle leaves a half bundle behind.
      if (!success) {
        console.log('=== ATTEMPTING ONE-BY-ONE ADD ===');

        let keysBefore = new Set();
        try {
          const snap = await (await fetch('/cart.js')).json();
          keysBefore = new Set((snap.items || []).map(i => i.key));
        } catch (e) {
          console.warn('Could not snapshot cart before one-by-one add:', e);
        }

        const rollback = async () => {
          try {
            const now = await (await fetch('/cart.js')).json();
            const mine = (now.items || []).filter(i =>
              !keysBefore.has(i.key) && (i.properties || {})._bundle_id === bundleId);
            for (const line of mine) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: 0 })
              });
            }
            console.log(`↩︎ Rolled back ${mine.length} partially added line(s)`);
          } catch (e) {
            console.error('Rollback after failed one-by-one add did not work:', e);
          }
        };

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          console.log(`Adding item ${i + 1}/${items.length}:`, item);

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(item)
            });

            const responseText = await response.text();
            console.log(`Item ${i + 1} response:`, responseText);

            if (!response.ok) {
              let errorData;
              try {
                errorData = JSON.parse(responseText);
              } catch (e) {
                errorData = { message: responseText };
              }

              const errorMessage = errorData.description || errorData.message || errorData.error || 'Unbekannter Fehler';
              throw new Error(`Fehler beim Hinzufügen von Artikel ${i + 1}: ${errorMessage}`);
            }

            console.log(`✓ Item ${i + 1} added successfully`);
          } catch (itemError) {
            console.error(`✗ Error adding item ${i + 1}:`, itemError);
            await rollback();
            throw new Error(`Fehler beim Hinzufügen von ${i === 0 ? 'Hauptprodukt' : 'Bundle-Artikel'}: ${itemError.message}`);
          }
        }

        console.log('✓ Successfully added all items one by one!');
        success = true;
      }

      if (success) {
        // First, trigger cart refresh events BEFORE clearing selections
        console.log('🔄 Triggering cart refresh events...');
        await this.refreshCartDrawer();

        // Clear selections and localStorage after successful cart addition
        this.selectedProducts.clear();
        this.userModifiedVariants.clear();
        localStorage.removeItem(this.storageKey());
        console.log('🗑️ Cleared selections and localStorage after successful cart addition');

        // Update all sections to remove selected items from display
        this.sections.forEach(section => {
          this.renderSelectedProductsForSection(section.sectionId);
        });

        // Show success message
        this.showNotification('Bundle erfolgreich zum Warenkorb hinzugefügt!');

        // Wait a moment for cart to update, then try to open drawer
        await new Promise(resolve => setTimeout(resolve, 300));

        // Try to open drawer cart instead of redirecting
        const drawerOpened = await this.openDrawerCart();

        if (drawerOpened) {
          // Refresh cart drawer content AFTER opening it
          // This ensures the drawer shows fresh content even if the theme re-renders on open
          await new Promise(resolve => setTimeout(resolve, 200));
          await this.refreshCartDrawer();
        } else {
          // Fallback: Redirect to cart page if no drawer cart found
          console.log('No drawer cart found, redirecting to cart page...');
          window.location.href = '/cart';
        }
      } else {
        throw new Error(batchError || 'Fehler beim Hinzufügen des Bundles zum Warenkorb');
      }
    } catch (error) {
      console.error('=== ERROR ADDING BUNDLE TO CART ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Bundle konnte nicht zum Warenkorb hinzugefügt werden. Bitte versuchen Sie es erneut.\n\nFehler: ${error.message}`);
    }
  }

  /**
   * Refresh cart drawer content after adding items
   */
  async refreshCartDrawer() {
    console.log('🔄 Refreshing cart drawer...');

    // Fetch fresh cart data to get updated item count
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      console.log('  ✓ Cart data refreshed:', cartData.item_count, 'items');
    } catch (error) {
      console.warn('  ⚠️ Could not fetch cart data:', error);
    }

    // Dispatch all common cart refresh events
    const refreshEvents = [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'cart-update',
      'ajaxCart.afterCartLoad',
      'theme:cart:change'
    ];

    refreshEvents.forEach(eventName => {
      // Try on different targets
      document.documentElement.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { source: 'bundle-app' } }));
      document.body.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { source: 'bundle-app' } }));
      window.dispatchEvent(new CustomEvent(eventName, { detail: { source: 'bundle-app' } }));
    });

    // Special handling for specific cart drawer implementations

    // 1. Dawn theme cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      console.log('  ✓ Found Dawn cart-drawer, refreshing...');
      if (typeof cartDrawer.renderContents === 'function') {
        try {
          const response = await fetch('/?sections=cart-drawer');
          const sections = await response.json();
          if (sections['cart-drawer']) {
            cartDrawer.renderContents(sections);
            console.log('  ✓ Dawn cart drawer refreshed');
          }
        } catch (error) {
          console.warn('  ⚠️ Could not refresh Dawn cart drawer:', error);
        }
      }
    }

    // 2. Cart notification/drawer elements
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      console.log('  ✓ Found cart-notification, refreshing...');
      try {
        const response = await fetch('/?sections=cart-notification');
        const sections = await response.json();
        if (sections['cart-notification']) {
          cartNotification.renderContents(sections);
          console.log('  ✓ Cart notification refreshed');
        }
      } catch (error) {
        console.warn('  ⚠️ Could not refresh cart notification:', error);
      }
    }

    // 3. Xclusive theme and similar - try multiple section fetches and replacements
    const drawerSelectors = [
      { selector: '#cart-drawer', section: 'cart-drawer' },
      { selector: '.cart-drawer', section: 'cart-drawer' },
      { selector: '[data-cart-drawer]', section: 'cart-drawer' },
      { selector: '.side-cart', section: 'side-cart' },
      { selector: '#side-cart', section: 'side-cart' },
      { selector: '.drawer--cart', section: 'cart-drawer' },
      { selector: '[data-ajax-cart]', section: 'ajax-cart' },
      { selector: '.ajax-cart', section: 'ajax-cart' },
      { selector: '#CartDrawer', section: 'cart-drawer' },
      { selector: '.mini-cart', section: 'mini-cart' }
    ];

    for (const { selector, section } of drawerSelectors) {
      const drawerElement = document.querySelector(selector);
      if (drawerElement) {
        console.log(`  ✓ Found drawer element: ${selector}, attempting section refresh...`);
        try {
          // Try fetching the section
          const response = await fetch(`/?sections=${section}`);
          if (response.ok) {
            const sections = await response.json();
            if (sections[section]) {
              const html = new DOMParser().parseFromString(sections[section], 'text/html');
              const newContent = html.querySelector(selector) || html.body.firstChild;
              if (newContent) {
                drawerElement.innerHTML = newContent.innerHTML;
                console.log(`  ✓ Drawer content refreshed via section: ${section}`);
              }
            }
          }
        } catch (error) {
          console.warn(`  ⚠️ Could not refresh drawer via ${section}:`, error);
        }
        break; // Stop after first match
      }
    }

    // 4. Try to call theme-specific refresh functions
    try {
      // Xclusive/common theme patterns
      if (typeof window.ajaxCart !== 'undefined') {
        if (typeof window.ajaxCart.load === 'function') {
          window.ajaxCart.load();
          console.log('  ✓ Called window.ajaxCart.load()');
        }
        if (typeof window.ajaxCart.refresh === 'function') {
          window.ajaxCart.refresh();
          console.log('  ✓ Called window.ajaxCart.refresh()');
        }
      }
      if (typeof window.CartDrawer !== 'undefined' && typeof window.CartDrawer.refresh === 'function') {
        window.CartDrawer.refresh();
        console.log('  ✓ Called window.CartDrawer.refresh()');
      }
      if (typeof window.Shopify?.theme?.cart?.refresh === 'function') {
        window.Shopify.theme.cart.refresh();
        console.log('  ✓ Called window.Shopify.theme.cart.refresh()');
      }
    } catch (error) {
      console.warn('  ⚠️ Theme-specific refresh failed:', error);
    }

    // 5. Update cart icon count if present
    const cartCountSelectors = [
      '.cart-count',
      '[data-cart-count]',
      '.cart-link__bubble',
      '.cart__count',
      '#cart-icon-bubble span'
    ];

    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const itemCount = cartData.item_count;

      cartCountSelectors.forEach(selector => {
        const countElements = document.querySelectorAll(selector);
        countElements.forEach(el => {
          el.textContent = itemCount;
          if (itemCount > 0) {
            el.classList.remove('hidden');
            el.style.display = '';
          }
        });
      });
    } catch (error) {
      console.warn('  ⚠️ Could not update cart count:', error);
    }

    console.log('  ✓ Cart refresh events dispatched');
  }

  /**
   * Try to open the drawer cart (Ajax cart) instead of redirecting
   * Returns true if drawer was opened, false if not found
   */
  async openDrawerCart() {
    console.log('🛒 ========== ATTEMPTING TO OPEN CART DRAWER ==========');

    // Debug: Log all potential cart elements on page
    console.log('  🔍 Debugging - scanning page for cart elements...');
    const allCartElements = document.querySelectorAll('[class*="cart"], [id*="cart"], [data-cart], cart-drawer, cart-notification');
    console.log(`  Found ${allCartElements.length} potential cart-related elements:`, allCartElements);

    // First, fetch fresh cart data to ensure drawer will show correct content
    try {
      console.log('  📦 Fetching fresh cart data...');
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      console.log('  ✓ Fresh cart data loaded:', cartData.item_count, 'items');
    } catch (error) {
      console.warn('  ⚠️ Could not fetch cart data:', error);
    }

    // Wait a brief moment to ensure cart data is processed
    await new Promise(resolve => setTimeout(resolve, 200));

    // Strategy 1: Try clicking cart icon/button directly (most reliable)
    console.log('  🎯 STRATEGY 1: Click cart icon/button directly');
    const cartIconSelectors = [
      'a[href="/cart"]',
      'a[href*="/cart"]',
      '.cart-link',
      '.header__cart',
      '.header-cart',
      '[data-cart-icon]',
      '.cart-icon',
      '#cart-icon',
      '.header__icon--cart',
      '.site-header__cart',
      '[aria-label*="cart" i]',
      '[aria-label*="Cart" i]',
      'button[data-cart]',
      '.js-drawer-open-cart'
    ];

    for (const selector of cartIconSelectors) {
      const cartIcon = document.querySelector(selector);
      if (cartIcon) {
        console.log(`  ✓ Found cart icon: ${selector}`, cartIcon);
        try {
          cartIcon.click();
          console.log('  ✓ Clicked cart icon - drawer should open!');
          await new Promise(resolve => setTimeout(resolve, 500));
          return true;
        } catch (error) {
          console.log('  ⚠️ Error clicking cart icon:', error);
        }
      }
    }

    // Strategy 2: Trigger common cart drawer events to refresh cart content
    console.log('  📢 STRATEGY 2: Dispatching cart update events...');
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    // Dawn theme and similar
    window.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    // Wait for drawer to process cart refresh events
    console.log('  ⏳ Waiting for drawer to refresh...');
    await new Promise(resolve => setTimeout(resolve, 300));

    // Strategy 3: Look for cart drawer custom element (Dawn theme)
    console.log('  🔍 STRATEGY 3: Dawn theme cart-drawer element');
    const cartDrawerElement = document.querySelector('cart-drawer');
    if (cartDrawerElement) {
      console.log('  ✓ Found cart-drawer custom element (Dawn theme)');
      try {
        // Try to call the open method if it exists
        if (typeof cartDrawerElement.open === 'function') {
          cartDrawerElement.open();
          console.log('  ✓ Called cartDrawerElement.open()');
          return true;
        }
        // Try to set the open attribute
        cartDrawerElement.setAttribute('open', '');
        console.log('  ✓ Set open attribute on cart-drawer');
        return true;
      } catch (error) {
        console.log('  ⚠️ Error opening Dawn cart drawer:', error);
      }
    }

    // Strategy 4: Look for common drawer cart elements
    console.log('  🔍 STRATEGY 4: Direct drawer element manipulation');
    const drawerSelectors = [
      '#cart',
      '.m6pn.toggle',
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '.js-drawer[data-drawer="cart"]',
      '#CartDrawer',
      '.drawer--cart',
      '[data-ajax-cart-drawer]',
      '.drawer[data-drawer-direction]',
      '#sidebar-cart',
      '.mini-cart'
    ];

    for (const selector of drawerSelectors) {
      const drawer = document.querySelector(selector);
      if (drawer) {
        console.log(`  ✓ Found drawer cart with selector: ${selector}`, drawer);
        try {
          // Try to add active/open class
          drawer.classList.add('active', 'open', 'is-open', 'drawer--is-open', 'cart-open');
          drawer.setAttribute('aria-hidden', 'false');
          drawer.style.display = 'block';
          drawer.style.visibility = 'visible';
          drawer.style.transform = 'translateX(0)';
          drawer.style.opacity = '1';

          // Also show overlay if present
          const overlay = document.querySelector('.drawer-overlay, .cart-overlay, [data-drawer-overlay]');
          if (overlay) {
            overlay.style.display = 'block';
            overlay.style.visibility = 'visible';
            overlay.style.opacity = '1';
          }

          console.log('  ✓ Opened drawer cart with classes and styles');
          return true;
        } catch (error) {
          console.log('  ⚠️ Error opening drawer:', error);
        }
      }
    }

    // Strategy 5: Look for cart drawer open button and click it
    console.log('  🔍 STRATEGY 5: Find and click drawer toggle buttons');
    const drawerOpenButtons = [
      '[href="#cart"]',
      '[data-cart-drawer-toggle]',
      '[data-open-cart]',
      '[data-cart-toggle]',
      '.js-cart-open',
      '[href="#cart-drawer"]',
      '[data-action="open-cart"]',
      '[aria-controls="cart-drawer"]',
      '[aria-controls="cart"]',
      '[aria-controls*="cart"]',
      '.cart-drawer-toggle',
      '#open-cart'
    ];

    for (const selector of drawerOpenButtons) {
      const button = document.querySelector(selector);
      if (button) {
        console.log(`  ✓ Found cart drawer button: ${selector}`, button);
        try {
          button.click();
          console.log('  ✓ Clicked cart drawer button');
          await new Promise(resolve => setTimeout(resolve, 500));
          return true;
        } catch (error) {
          console.log('  ⚠️ Error clicking button:', error);
        }
      }
    }

    // Strategy 6: Check for theme-specific cart drawer functions
    console.log('  🔍 STRATEGY 6: Theme-specific cart functions');
    if (typeof window.theme !== 'undefined') {
      console.log('  Found window.theme object');
      if (typeof window.theme.openCartDrawer === 'function') {
        window.theme.openCartDrawer();
        console.log('  ✓ Called window.theme.openCartDrawer()');
        return true;
      }
      if (typeof window.theme.cart?.open === 'function') {
        window.theme.cart.open();
        console.log('  ✓ Called window.theme.cart.open()');
        return true;
      }
      if (typeof window.theme.CartDrawer?.open === 'function') {
        window.theme.CartDrawer.open();
        console.log('  ✓ Called window.theme.CartDrawer.open()');
        return true;
      }
    }

    // Strategy 7: Check for Slate/Timber framework functions
    console.log('  🔍 STRATEGY 7: Slate/Timber framework');
    if (typeof window.timber !== 'undefined' && typeof window.timber.RightDrawer === 'function') {
      console.log('  ✓ Found Timber framework');
      try {
        window.timber.RightDrawer.open();
        console.log('  ✓ Called window.timber.RightDrawer.open()');
        return true;
      } catch (error) {
        console.log('  ⚠️ Error opening Timber drawer:', error);
      }
    }

    // Strategy 8: Dispatch additional common events as last resort
    console.log('  📢 STRATEGY 8: Dispatching additional drawer-specific events...');
    document.documentElement.dispatchEvent(new CustomEvent('drawer:open', { detail: { drawer: 'cart' }, bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:open'));
    window.dispatchEvent(new CustomEvent('openCart'));

    // Wait and check if any of the events were caught
    await new Promise(resolve => setTimeout(resolve, 500));

    const drawerIsVisible = drawerSelectors.some(selector => {
      const el = document.querySelector(selector);
      return el && (
        el.classList.contains('active') ||
        el.classList.contains('open') ||
        el.classList.contains('is-open') ||
        el.style.display === 'block' ||
        el.offsetParent !== null
      );
    });

    if (drawerIsVisible) {
      console.log('  ✓ Drawer appears to be open after events');
      return true;
    }

    // No drawer found - log summary
    console.log('  ❌ Could not find or open cart drawer');
    console.log('  💡 TIP: Check the console logs above to see what cart elements were found');
    console.log('  💡 Share these logs with support to add custom cart drawer support for your theme');
    console.log('🛒 ========== CART DRAWER ATTEMPT COMPLETE ==========');

    return false;
  }

  formatMoney(priceInput) {
    let cents;
    if (typeof priceInput === 'string') {
      // Check if the string contains a decimal point (dollar format like "19.99")
      // or is a whole number (cents format like "1999")
      if (priceInput.includes('.')) {
        // Dollar format with decimal
        const dollars = parseFloat(priceInput);
        cents = Math.round(dollars * 100);
      } else {
        // Already in cents as string (e.g., "1999")
        cents = parseInt(priceInput, 10) || 0;
      }
    } else {
      // Already in cents as number (e.g., 1999)
      cents = priceInput;
    }

    // Always use German format: 1.000,00€ (NF: kein Leerzeichen vor €)
    const germanFormatted = this.formatWithThousandsSeparator(cents / 100, ',', '.') + '€';
    return germanFormatted;
  }

  formatWithThousandsSeparator(number, decimalSep, thousandsSep) {
    const parts = number.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return parts.join(decimalSep);
  }
  };
}

// Initialize bundle selectors when DOM is ready
// This runs outside the class definition block to ensure it always runs
// NF UX-Test: nf-bundle-ux.js erweitert die Klasse und startet danach selbst.
if (!window.NF_BUNDLE_UX) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleSelectors);
  } else {
    initBundleSelectors();
  }
}

function initBundleSelectors() {
  // Prevent multiple initializations
  if (window.bundleSelectorsInitialized) {
    console.log('Bundle selectors already initialized, skipping...');
    return;
  }
  window.bundleSelectorsInitialized = true;

  console.log('Initializing bundle selectors...');
  const bundleBlocks = document.querySelectorAll('[data-bundle-block]');
  console.log('Found bundle blocks:', bundleBlocks.length);

  // Warn if multiple blocks detected (possible duplicate in theme editor)
  if (bundleBlocks.length > 1) {
    console.warn('⚠️ WARNING: Multiple bundle selector blocks detected!');
    console.warn('  This may cause the selector to appear twice.');
    console.warn('  Check your Shopify theme customizer and ensure only ONE bundle block is added.');
    console.warn('  Bundle blocks found:', bundleBlocks);
  }

  bundleBlocks.forEach((block, index) => {
    const blockId = block.dataset.blockId;
    const settings = window.bundleSettings?.[blockId];

    if (settings) {
      console.log(`Creating BundleSelector instance ${index + 1} for block:`, blockId);
      new window.BundleSelector(block, settings);
    }
  });
}
